import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  Users, Activity, DollarSign, ShoppingCart, Download, Loader2,
  Monitor, Smartphone, Tablet, Globe, FileText, Tag, BarChart3, Eye, ChevronDown, ChevronRight,
  RefreshCw, TrendingUp, Zap, ShoppingBag, MapPin, ArrowRight, ArrowDown as ArrowDownIcon
} from 'lucide-react';

const API = 'https://api.glowytics.com/api/bq-analytics';
const COLORS = ['#1f77b4','#ff7f0e','#2ca02c','#d62728','#9467bd','#8c564b','#e377c2','#7f7f7f','#bcbd22','#17becf'];

const TABS = [
  { key: 'overview', label: 'Overview', icon: BarChart3 },
  { key: 'traffic', label: 'Traffic Sources', icon: Globe },
  { key: 'devices', label: 'Devices & Geo', icon: Monitor },
  { key: 'pages', label: 'Pages', icon: FileText },
  { key: 'events', label: 'Events', icon: Zap },
  { key: 'ecommerce', label: 'E-commerce', icon: ShoppingCart },
  { key: 'banners', label: 'Banner Performance', icon: Tag },
];

const PRESETS = [
  { label: '7 Days', key: 'last7' },
  { label: '30 Days', key: 'last30' },
  { label: '90 Days', key: 'last90' },
  { label: 'This Month', key: 'thisMonth' },
  { label: 'Last Month', key: 'lastMonth' },
  { label: 'Custom', key: 'custom' },
];

function getDateRange(key) {
  const today = new Date();
  const fmt = d => d.toISOString().slice(0, 10);
  switch (key) {
    case 'last7': {
      const s = new Date(today); s.setDate(s.getDate() - 7);
      return { startDate: fmt(s), endDate: fmt(today) };
    }
    case 'last30': {
      const s = new Date(today); s.setDate(s.getDate() - 30);
      return { startDate: fmt(s), endDate: fmt(today) };
    }
    case 'last90': {
      const s = new Date(today); s.setDate(s.getDate() - 90);
      return { startDate: fmt(s), endDate: fmt(today) };
    }
    case 'thisMonth': {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      return { startDate: fmt(s), endDate: fmt(today) };
    }
    case 'lastMonth': {
      const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const e = new Date(today.getFullYear(), today.getMonth(), 0);
      return { startDate: fmt(s), endDate: fmt(e) };
    }
    default:
      return { startDate: '2025-01-01', endDate: fmt(today) };
  }
}

function fmtNum(n) {
  if (n == null) return '0';
  return Number(n).toLocaleString('en-US');
}

function fmtCur(n) {
  if (n == null) return '₺0';
  return '₺' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function downloadCSV(rows, filename) {
  if (!rows || rows.length === 0) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(','), ...rows.map(r => keys.map(k => JSON.stringify(r[k] ?? '')).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

async function apiFetch(endpoint, startDate, endDate) {
  const brandId = localStorage.getItem('currentBrandId');
  const res = await fetch(`${API}/${endpoint}?startDate=${startDate}&endDate=${endDate}`, {
    credentials: 'include',
    headers: { 'X-Brand-ID': brandId || '' }
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

const tooltipStyle = { backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, color: '#1f2937', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.07)' };

/* ── KPI Card ── */
function KPICard({ icon: Icon, label, value, variant = 'default' }) {
  if (variant === 'primary') {
    return (
      <div className="p-4 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-xl border border-blue-400">
        <div className="flex justify-between items-start">
          <div>
            <p className="text-blue-100 text-xs">{label}</p>
            <p className="text-xl font-semibold mt-1">{value}</p>
          </div>
          <div className="p-2 bg-white/10 rounded-lg">
            <Icon className="w-4 h-4" />
          </div>
        </div>
      </div>
    );
  }
  const iconColors = {
    Users: { bg: 'bg-blue-50', text: 'text-blue-600' },
    Activity: { bg: 'bg-cyan-50', text: 'text-cyan-600' },
    DollarSign: { bg: 'bg-green-50', text: 'text-green-600' },
    ShoppingCart: { bg: 'bg-purple-50', text: 'text-purple-600' },
    TrendingUp: { bg: 'bg-orange-50', text: 'text-orange-600' },
    Zap: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
  };
  const ic = iconColors[Icon.displayName || Icon.name] || { bg: 'bg-blue-50', text: 'text-blue-600' };
  return (
    <div className="p-4 rounded-xl shadow-xl border border-gray-100 bg-white">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-gray-500 text-xs">{label}</p>
          <p className="text-xl font-semibold mt-1 text-gray-900">{value}</p>
        </div>
        <div className={`p-2 ${ic.bg} rounded-lg`}>
          <Icon className={`w-4 h-4 ${ic.text}`} />
        </div>
      </div>
    </div>
  );
}

/* ── Sortable Table ── */
function SortableTable({ columns, data, csvFilename }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  const numericKeys = useMemo(() => {
    const keys = new Set();
    columns.forEach(col => {
      if (col.render) keys.add(col.key);
    });
    return keys;
  }, [columns]);

  const sorted = useMemo(() => {
    if (!sortKey || !data) return data || [];
    return [...data].sort((a, b) => {
      const va = a[sortKey], vb = b[sortKey];
      const na = Number(va), nb = Number(vb);
      if (!isNaN(na) && !isNaN(nb)) return sortDir === 'asc' ? na - nb : nb - na;
      return sortDir === 'asc' ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va));
    });
  }, [data, sortKey, sortDir]);

  const totals = useMemo(() => {
    if (!data || data.length === 0) return null;
    const sums = {};
    numericKeys.forEach(key => { sums[key] = 0; });
    data.forEach(row => {
      numericKeys.forEach(key => {
        const v = Number(row[key] || 0);
        if (isFinite(v)) sums[key] += v;
      });
    });
    return sums;
  }, [data, numericKeys]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const displayed = sorted.slice(0, 200);

  return (
    <div>
      {csvFilename && data && data.length > 0 && (
        <div className="flex justify-end mb-2">
          <button onClick={() => downloadCSV(data, csvFilename)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition-colors shadow-sm">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left">
              {columns.map((col, idx) => {
                const isNum = numericKeys.has(col.key);
                return (
                  <th key={col.key}
                    className={`px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap cursor-pointer hover:text-gray-900 select-none transition-colors ${isNum ? 'text-right' : 'text-left'}`}
                    onClick={() => toggleSort(col.key)}>
                    {col.label} {sortKey === col.key ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y">
            {displayed.length === 0 ? (
              <tr><td colSpan={columns.length} className="text-center py-8 text-gray-400 text-sm">No data found</td></tr>
            ) : displayed.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50 transition-colors">
                {columns.map((col) => {
                  const isNum = numericKeys.has(col.key);
                  return (
                    <td key={col.key} className={`px-4 py-2 text-sm ${isNum ? 'text-right text-gray-900' : 'text-left text-gray-800'}`}>
                      {col.render ? col.render(row[col.key], row) : row[col.key]}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          {totals && numericKeys.size > 0 && (
            <tfoot className="sticky bottom-0 bg-white z-10">
              <tr className="border-t-2 border-gray-200">
                {columns.map((col, idx) => {
                  const isNum = numericKeys.has(col.key);
                  if (idx === 0) return <td key={col.key} className="px-4 py-3 text-left font-semibold text-gray-900">TOTAL</td>;
                  return (
                    <td key={col.key} className={`px-4 py-3 text-sm font-semibold ${isNum ? 'text-right' : 'text-left'}`}>
                      {isNum && col.render ? col.render(totals[col.key]) : ''}
                    </td>
                  );
                })}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {sorted.length > 200 && <p className="text-xs text-gray-400 mt-2 text-right">Showing 200 of {sorted.length.toLocaleString()} rows</p>}
    </div>
  );
}

/* ── Overview: Event / Geo / Page / E-commerce summary tables (BigQuery) ── */
function OverviewBqDetailSummaries({ eventsData, geoData, pagesData, ecommerceData }) {
  const evRows = (eventsData?.rows || []).slice(0, 10);
  const geoRows = (geoData?.rows || []).slice(0, 10);
  const pageRows = (pagesData?.rows || []).slice(0, 10);
  const ecKpi = ecommerceData?.kpi;
  const ecRows = (ecommerceData?.rows || []).slice(0, 8);

  const totalEventHits = (eventsData?.rows || []).reduce((s, r) => s + Number(r.event_count || 0), 0);
  const uniqueEventTypes = (eventsData?.rows || []).length;
  const geoSessions = (geoData?.rows || []).reduce((s, r) => s + Number(r.sessions || 0), 0);
  const geoUsers = (geoData?.rows || []).reduce((s, r) => s + Number(r.users || 0), 0);
  const totalPageviews = (pagesData?.rows || []).reduce((s, r) => s + Number(r.pageviews || 0), 0);
  const uniquePages = (pagesData?.rows || []).length;

  const Panel = ({ title, icon: Icon, accent, children }) => (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col h-full">
      <div className={`px-4 py-3 border-b flex items-center gap-2 ${accent}`}>
        <Icon className="w-4 h-4" />
        <h4 className="font-semibold text-sm text-gray-900">{title}</h4>
        <span className="ml-auto text-[10px] font-medium uppercase tracking-wide text-blue-600">BigQuery</span>
      </div>
      <div className="p-4 flex-1 overflow-x-auto">{children}</div>
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">GA4 detay özeti</h3>
        <p className="text-xs text-gray-500 mt-0.5">
          Seçilen dönem için event, coğrafya, sayfa ve e-ticaret tablolarından özet (aynı tarih aralığı)
        </p>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Panel title="Events (event sayıları)" icon={Zap} accent="bg-amber-50 border-amber-100">
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="rounded-lg bg-gray-50 px-2 py-1.5 text-center">
              <p className="text-[10px] text-gray-500">Toplam tetik</p>
              <p className="text-sm font-semibold text-gray-900">{fmtNum(totalEventHits)}</p>
            </div>
            <div className="rounded-lg bg-gray-50 px-2 py-1.5 text-center">
              <p className="text-[10px] text-gray-500">Farklı event</p>
              <p className="text-sm font-semibold text-gray-900">{fmtNum(uniqueEventTypes)}</p>
            </div>
            <div className="rounded-lg bg-gray-50 px-2 py-1.5 text-center">
              <p className="text-[10px] text-gray-500">En çok</p>
              <p className="text-xs font-medium text-gray-800 truncate" title={evRows[0]?.event_name}>{evRows[0]?.event_name || '—'}</p>
            </div>
          </div>
          {evRows.length === 0 ? (
            <p className="text-sm text-gray-400">Veri yok</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2 pr-2">Event</th>
                  <th className="pb-2 text-right">Adet</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {evRows.map((r, i) => (
                  <tr key={i}>
                    <td className="py-1.5 pr-2 text-gray-800 max-w-[180px] truncate" title={r.event_name}>{r.event_name}</td>
                    <td className="py-1.5 text-right font-medium text-gray-900">{fmtNum(r.event_count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title="Geo (ülke / bölge)" icon={MapPin} accent="bg-sky-50 border-sky-100">
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="rounded-lg bg-gray-50 px-2 py-1.5">
              <p className="text-[10px] text-gray-500">Sessions</p>
              <p className="text-sm font-semibold text-gray-900">{fmtNum(geoSessions)}</p>
            </div>
            <div className="rounded-lg bg-gray-50 px-2 py-1.5">
              <p className="text-[10px] text-gray-500">Users</p>
              <p className="text-sm font-semibold text-gray-900">{fmtNum(geoUsers)}</p>
            </div>
          </div>
          {geoRows.length === 0 ? (
            <p className="text-sm text-gray-400">Veri yok</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2">Ülke</th>
                  <th className="pb-2">Şehir</th>
                  <th className="pb-2 text-right">Sess.</th>
                  <th className="pb-2 text-right">Ciro</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {geoRows.map((r, i) => (
                  <tr key={i}>
                    <td className="py-1.5 text-gray-800">{r.country || '—'}</td>
                    <td className="py-1.5 text-gray-600 max-w-[100px] truncate" title={r.city}>{r.city || '—'}</td>
                    <td className="py-1.5 text-right">{fmtNum(r.sessions)}</td>
                    <td className="py-1.5 text-right text-green-700">{fmtCur(r.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title="Pages (sayfa performansı)" icon={FileText} accent="bg-violet-50 border-violet-100">
          <div className="grid grid-cols-2 gap-2 mb-3">
            <div className="rounded-lg bg-gray-50 px-2 py-1.5">
              <p className="text-[10px] text-gray-500">Pageviews</p>
              <p className="text-sm font-semibold text-gray-900">{fmtNum(totalPageviews)}</p>
            </div>
            <div className="rounded-lg bg-gray-50 px-2 py-1.5">
              <p className="text-[10px] text-gray-500">Sayfa satırı</p>
              <p className="text-sm font-semibold text-gray-900">{fmtNum(uniquePages)}</p>
            </div>
          </div>
          {pageRows.length === 0 ? (
            <p className="text-sm text-gray-400">Veri yok</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2">Path</th>
                  <th className="pb-2 text-right">PV</th>
                  <th className="pb-2 text-right">Unique</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pageRows.map((r, i) => (
                  <tr key={i}>
                    <td className="py-1.5 text-gray-800 max-w-[220px] truncate" title={r.page_path}>{r.page_path}</td>
                    <td className="py-1.5 text-right">{fmtNum(r.pageviews)}</td>
                    <td className="py-1.5 text-right text-gray-600">{fmtNum(r.unique_pageviews)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>

        <Panel title="E-commerce (işlemler)" icon={ShoppingBag} accent="bg-emerald-50 border-emerald-100">
          {ecKpi && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              <div className="rounded-lg bg-gray-50 px-2 py-1.5 text-center">
                <p className="text-[10px] text-gray-500">Ciro</p>
                <p className="text-sm font-semibold text-green-700">{fmtCur(ecKpi.totalRevenue)}</p>
              </div>
              <div className="rounded-lg bg-gray-50 px-2 py-1.5 text-center">
                <p className="text-[10px] text-gray-500">İşlem</p>
                <p className="text-sm font-semibold text-gray-900">{fmtNum(ecKpi.totalTransactions)}</p>
              </div>
              <div className="rounded-lg bg-gray-50 px-2 py-1.5 text-center">
                <p className="text-[10px] text-gray-500">AOV</p>
                <p className="text-sm font-semibold text-gray-900">{fmtCur(ecKpi.aov)}</p>
              </div>
              <div className="rounded-lg bg-gray-50 px-2 py-1.5 text-center">
                <p className="text-[10px] text-gray-500">Ürün adedi</p>
                <p className="text-sm font-semibold text-gray-900">{fmtNum(ecKpi.totalItems)}</p>
              </div>
            </div>
          )}
          {ecRows.length === 0 ? (
            <p className="text-sm text-gray-400">Satır yok (dönemde purchase kaydı)</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="pb-2">Tarih</th>
                  <th className="pb-2">İşlem ID</th>
                  <th className="pb-2 text-right">Ciro</th>
                  <th className="pb-2 text-right">Adet</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {ecRows.map((r, i) => (
                  <tr key={i}>
                    <td className="py-1.5 text-gray-700 whitespace-nowrap">{r.date}</td>
                    <td className="py-1.5 text-gray-600 max-w-[120px] truncate" title={r.transactionId}>{r.transactionId || '—'}</td>
                    <td className="py-1.5 text-right font-medium text-green-700">{fmtCur(r.revenue)}</td>
                    <td className="py-1.5 text-right">{fmtNum(r.itemQuantity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>
    </div>
  );
}

/* ── Tab: Overview ── */
function OverviewTab({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [eventsData, setEventsData] = useState(null);
  const [geoData, setGeoData] = useState(null);
  const [pagesData, setPagesData] = useState(null);
  const [ecommerceData, setEcommerceData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      apiFetch('summary', startDate, endDate),
      apiFetch('event-counts', startDate, endDate),
      apiFetch('geo', startDate, endDate),
      apiFetch('pages', startDate, endDate),
      apiFetch('ecommerce', startDate, endDate),
    ])
      .then(([summary, ev, geo, pages, ec]) => {
        setData(summary);
        setEventsData(ev);
        setGeoData(geo);
        setPagesData(pages);
        setEcommerceData(ec);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading) return <Spinner />;

  const hasSummaryKpi = data?.kpi != null;
  const hasDetailBlocks =
    (eventsData?.rows?.length > 0) ||
    (geoData?.rows?.length > 0) ||
    (pagesData?.rows?.length > 0) ||
    (ecommerceData?.rows?.length > 0) ||
    (ecommerceData?.kpi && (Number(ecommerceData.kpi.totalTransactions) > 0 || Number(ecommerceData.kpi.totalRevenue) > 0));

  if (!hasSummaryKpi && !hasDetailBlocks) return <Empty />;

  const { kpi, timeseries } = data || { kpi: null, timeseries: [] };

  return (
    <div className="space-y-8">
      {hasSummaryKpi && (
        <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard icon={Users} label="Users" value={fmtNum(kpi.totalUsers)} variant="primary" />
        <KPICard icon={Activity} label="Sessions" value={fmtNum(kpi.totalSessions)} />
        <KPICard icon={DollarSign} label="Revenue" value={fmtCur(kpi.totalRevenue)} />
        <KPICard icon={ShoppingCart} label="Transactions" value={fmtNum(kpi.totalTransactions)} />
      </div>

      {/* Users + Sessions Line Chart */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b">
          <h3 className="font-semibold text-lg text-gray-800">Daily Users & Sessions Trend</h3>
        </div>
        <div className="p-6">
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={timeseries} margin={{ top: 10, right: 20, bottom: 5, left: 10 }}>
              <defs>
                <linearGradient id="gradUsers" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.15} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradSessions" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#06b6d4" stopOpacity={0.12} />
                  <stop offset="100%" stopColor="#06b6d4" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 11 }}
                tickFormatter={v => { const d = new Date(v); return `${d.getDate()} ${d.toLocaleString('tr-TR', { month: 'short' })}`; }}
                axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
              <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} axisLine={false} tickLine={false}
                tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(0)}K` : v} />
              <Tooltip contentStyle={tooltipStyle}
                labelFormatter={v => { const d = new Date(v); return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }); }}
                formatter={(v, name) => [fmtNum(v), name]} />
              <Legend iconType="circle" wrapperStyle={{ paddingTop: 12 }} />
              <Line type="monotone" dataKey="users" stroke="#6366f1" name="Users" strokeWidth={2.5}
                dot={false} activeDot={{ r: 5, strokeWidth: 2, fill: '#fff' }} fill="url(#gradUsers)" />
              <Line type="monotone" dataKey="sessions" stroke="#06b6d4" name="Sessions" strokeWidth={2.5}
                dot={false} activeDot={{ r: 5, strokeWidth: 2, fill: '#fff' }} fill="url(#gradSessions)" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Revenue + Transactions Bar Chart */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b">
          <h3 className="font-semibold text-lg text-gray-800">Daily Revenue & Transactions</h3>
        </div>
        <div className="p-6">
          <ResponsiveContainer width="100%" height={360}>
            <BarChart data={timeseries} margin={{ top: 10, right: 20, bottom: 5, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: '#9ca3af', fontSize: 11 }}
                tickFormatter={v => { const d = new Date(v); return `${d.getDate()} ${d.toLocaleString('tr-TR', { month: 'short' })}`; }}
                axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
              <YAxis yAxisId="left" tick={{ fill: '#9ca3af', fontSize: 11 }} axisLine={false} tickLine={false}
                tickFormatter={v => v >= 1000 ? `₺${(v/1000).toFixed(0)}K` : `₺${v}`} />
              <YAxis yAxisId="right" orientation="right" tick={{ fill: '#9ca3af', fontSize: 11 }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={tooltipStyle}
                labelFormatter={v => { const d = new Date(v); return d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }); }}
                formatter={(v, name) => [name === 'Revenue' ? fmtCur(v) : fmtNum(v), name]} />
              <Legend iconType="circle" wrapperStyle={{ paddingTop: 12 }} />
              <Bar yAxisId="left" dataKey="revenue" fill="#10b981" name="Revenue" radius={[4,4,0,0]} fillOpacity={0.85} />
              <Bar yAxisId="right" dataKey="transactions" fill="#f59e0b" name="Transactions" radius={[4,4,0,0]} fillOpacity={0.85} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {timeseries.length > 0 && (
        <div className="flex justify-end">
          <button onClick={() => downloadCSV(timeseries, 'bq-summary.csv')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition-colors shadow-sm">
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
        </div>
      )}
        </>
      )}

      {hasDetailBlocks && (
        <OverviewBqDetailSummaries
          eventsData={eventsData}
          geoData={geoData}
          pagesData={pagesData}
          ecommerceData={ecommerceData}
        />
      )}
    </div>
  );
}

/* ── Tab: Traffic Sources (SourceMediumMonthly style) ── */
function TrafficTab({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [dailyData, setDailyData] = useState([]);
  const [summaryKpi, setSummaryKpi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSource, setSelectedSource] = useState(null);
  const [expandedSources, setExpandedSources] = useState({});
  const [primaryMetric, setPrimaryMetric] = useState('revenue');
  const [secondaryMetric, setSecondaryMetric] = useState('none');

  // Compact formatters
  const fmtCompact = (n) => {
    const v = Number(n) || 0;
    if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `${Math.round(v / 1_000)}K`;
    return v.toLocaleString('en-US');
  };
  const fmtCompactCur = (n) => {
    const v = Number(n) || 0;
    if (v >= 1_000_000) return `₺${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `₺${Math.round(v / 1_000)}K`;
    return `₺${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  };

  useEffect(() => {
    setLoading(true);
    Promise.all([
      apiFetch('traffic-sources', startDate, endDate),
      apiFetch('traffic-sources-daily', startDate, endDate),
      apiFetch('summary', startDate, endDate),
    ])
      .then(([d, daily, summaryData]) => {
        setData(d);
        setDailyData(daily?.rows || []);
        setSummaryKpi(summaryData?.kpi || null);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  // Group rows by source/medium, with campaigns underneath
  const sources = useMemo(() => {
    if (!data?.rows?.length) return [];
    const map = {};
    for (const r of data.rows) {
      const key = `${r.source || '(direct)'} / ${r.medium || '(none)'}`;
      if (!map[key]) map[key] = { key, source: key, sessions: 0, users: 0, transactions: 0, revenue: 0, campaigns: [] };
      map[key].sessions += Number(r.sessions || 0);
      map[key].users += Number(r.users || 0);
      map[key].transactions += Number(r.transactions || 0);
      map[key].revenue += Number(r.revenue || 0);
      if (r.campaign && r.campaign !== '(not set)' && r.campaign !== '(organic)' && r.campaign !== '(direct)') {
        map[key].campaigns.push({
          name: r.campaign,
          sessions: Number(r.sessions || 0),
          conversions: Number(r.transactions || 0),
          revenue: Number(r.revenue || 0),
          conversion_rate: Number(r.sessions) > 0 ? ((Number(r.transactions) / Number(r.sessions)) * 100).toFixed(2) : '0.00',
        });
      }
    }
    return Object.values(map)
      .map(s => ({
        ...s,
        conversion_rate: s.sessions > 0 ? ((s.transactions / s.sessions) * 100).toFixed(2) : '0.00',
      }))
      .sort((a, b) => b.sessions - a.sessions);
  }, [data]);

  // Summary totals
  const summary = useMemo(() => {
    return sources.reduce((acc, s) => {
      acc.sessions += s.sessions;
      acc.users += s.users;
      acc.transactions += s.transactions;
      acc.revenue += s.revenue;
      return acc;
    }, { sessions: 0, users: 0, transactions: 0, revenue: 0 });
  }, [sources]);

  // Trend chart data for selected source
  const chartData = useMemo(() => {
    if (!dailyData.length) return { labels: [], datasets: [] };
    const filtered = selectedSource
      ? dailyData.filter(r => `${r.source || '(direct)'} / ${r.medium || '(none)'}` === selectedSource.key)
      : dailyData;

    // Aggregate by date
    const dayMap = {};
    for (const r of filtered) {
      const d = r.date;
      if (!dayMap[d]) dayMap[d] = { sessions: 0, transactions: 0, revenue: 0 };
      dayMap[d].sessions += Number(r.sessions || 0);
      dayMap[d].transactions += Number(r.transactions || 0);
      dayMap[d].revenue += Number(r.revenue || 0);
    }
    const sorted = Object.entries(dayMap).sort(([a], [b]) => a.localeCompare(b));
    const labels = sorted.map(([d]) => { const dt = new Date(d); return `${dt.getDate()} ${dt.toLocaleString('default', { month: 'short' })}`; });

    const metricData = (metric) => sorted.map(([, v]) => {
      if (metric === 'revenue') return Math.round(v.revenue);
      if (metric === 'sessions') return v.sessions;
      if (metric === 'conversions') return v.transactions;
      if (metric === 'cr') return v.sessions > 0 ? Number(((v.transactions / v.sessions) * 100).toFixed(2)) : 0;
      return 0;
    });

    const datasets = [];
    const metricLabel = (m) => m === 'revenue' ? 'Revenue' : m === 'sessions' ? 'Sessions' : m === 'conversions' ? 'Conversions' : 'CR (%)';
    datasets.push({
      label: metricLabel(primaryMetric),
      data: metricData(primaryMetric),
      borderColor: '#2563EB',
      backgroundColor: 'rgba(37, 99, 235, 0.15)',
      fill: true,
      tension: 0.4,
    });
    if (secondaryMetric !== 'none' && secondaryMetric !== primaryMetric) {
      datasets.push({
        label: metricLabel(secondaryMetric),
        data: metricData(secondaryMetric),
        borderColor: '#7C3AED',
        backgroundColor: 'rgba(124, 58, 237, 0.10)',
        fill: false,
        tension: 0.4,
      });
    }
    return { labels, datasets };
  }, [dailyData, selectedSource, primaryMetric, secondaryMetric]);

  // Monthly mini charts
  const monthlyCharts = useMemo(() => {
    if (!dailyData.length) return { sessions: [], purchases: [], revenue: [], cr: [] };
    const monthMap = {};
    for (const r of dailyData) {
      const d = new Date(r.date);
      const mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const ml = d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      if (!monthMap[mk]) monthMap[mk] = { label: ml, sessions: 0, transactions: 0, revenue: 0 };
      monthMap[mk].sessions += Number(r.sessions || 0);
      monthMap[mk].transactions += Number(r.transactions || 0);
      monthMap[mk].revenue += Number(r.revenue || 0);
    }
    const sorted = Object.entries(monthMap).sort(([a], [b]) => a.localeCompare(b));
    return {
      labels: sorted.map(([, v]) => v.label),
      sessions: sorted.map(([, v]) => v.sessions),
      purchases: sorted.map(([, v]) => v.transactions),
      revenue: sorted.map(([, v]) => Math.round(v.revenue)),
      cr: sorted.map(([, v]) => v.sessions > 0 ? Number(((v.transactions / v.sessions) * 100).toFixed(2)) : 0),
    };
  }, [dailyData]);

  if (loading) return <Spinner />;
  if (!data?.rows?.length) return <Empty />;

  const filteredSources = sources.filter(s =>
    s.source.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleExpand = (key) => {
    setExpandedSources(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSourceClick = (source) => {
    setSelectedSource(prev => prev?.key === source.key ? null : source);
  };

  const convRate = summary.sessions > 0 ? ((summary.transactions / summary.sessions) * 100).toFixed(2) : '0.00';

  return (
    <div className="space-y-6">
      {/* KPI Cards - use summary (bq_ga4_daily_summary) for accurate totals */}
      {(() => {
        const kpi = summaryKpi || {};
        const s = Number(kpi.totalSessions || 0);
        const u = Number(kpi.totalUsers || 0);
        const t = Number(kpi.totalTransactions || 0);
        const r = Number(kpi.totalRevenue || 0);
        const cr = s > 0 ? ((t / s) * 100).toFixed(2) : '0.00';
        const rpc = t > 0 ? r / t : 0;
        return (
          <div className="grid grid-cols-6 gap-4">
            <KPICard icon={Users} label="Total Sessions" value={fmtNum(s)} variant="primary" />
            <KPICard icon={Globe} label="Users" value={fmtNum(u)} />
            <KPICard icon={ShoppingCart} label="Conversions" value={fmtNum(t)} />
            <KPICard icon={Zap} label="Conversion Rate" value={cr + '%'} />
            <KPICard icon={TrendingUp} label="Rev. per Conv." value={fmtCur(rpc)} />
            <KPICard icon={DollarSign} label="Total Revenue" value={fmtCur(r)} />
          </div>
        );
      })()}

      {/* Main Grid: 4/8 layout */}
      <div className="grid grid-cols-12 gap-6">
        {/* Left: Source list */}
        <div className="col-span-12 lg:col-span-4">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden h-full">
            <div className="p-4 border-b">
              <h2 className="font-semibold text-sm text-gray-800">Source & Medium</h2>
              <div className="mt-3 relative w-full">
                <input
                  type="text"
                  placeholder="Search sources..."
                  className="pl-9 pr-4 py-2 bg-gray-50 border rounded-lg text-sm w-full"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              </div>
            </div>

            <div className="overflow-x-auto overflow-y-auto max-h-[900px]">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th className="pl-3 pr-3 py-1.5 text-[9px] font-medium text-gray-500">SOURCE / CAMPAIGN</th>
                    <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500">SESSIONS</th>
                    <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500">Conv.</th>
                    <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500">CR</th>
                    <th className="px-3 py-1.5 text-[9px] font-medium text-gray-500">REVENUE</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredSources.map((source, index) => (
                    <React.Fragment key={source.key}>
                      <tr
                        className={`hover:bg-gray-50 transition-colors cursor-pointer ${selectedSource?.key === source.key ? 'bg-blue-50' : ''}`}
                      >
                        <td className="pl-3 pr-3 py-1 whitespace-normal" onClick={() => handleSourceClick(source)}>
                          <div className="flex items-center gap-2">
                            {source.campaigns.length > 0 ? (
                              <button
                                onClick={(e) => { e.stopPropagation(); toggleExpand(source.key); }}
                                className="text-gray-400 hover:text-blue-600 flex-shrink-0"
                              >
                                {expandedSources[source.key]
                                  ? <ChevronDown size={16} />
                                  : <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                }
                              </button>
                            ) : <div className="w-4 h-4 flex-shrink-0" />}
                            <div className="flex items-center gap-2">
                              <div className="p-1.5 rounded-md bg-gray-100">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index % COLORS.length] }} />
                              </div>
                              <span className="font-medium text-[10px] text-gray-900 break-words leading-tight min-w-0">{source.source}</span>
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap cursor-pointer" onClick={() => handleSourceClick(source)}>
                          <div className="font-medium text-[10px] text-gray-900">{fmtCompact(source.sessions)}</div>
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap cursor-pointer" onClick={() => handleSourceClick(source)}>
                          <div className="font-medium text-[10px] text-gray-900">{source.transactions.toLocaleString()}</div>
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap cursor-pointer" onClick={() => handleSourceClick(source)}>
                          <div className={`font-medium text-[10px] ${Number(source.conversion_rate) >= 3 ? 'text-green-600' : 'text-gray-900'}`}>
                            {source.conversion_rate}%
                          </div>
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap cursor-pointer" onClick={() => handleSourceClick(source)}>
                          <div className="font-medium text-[10px] text-gray-900">{fmtCompactCur(source.revenue)}</div>
                        </td>
                      </tr>

                      {/* Expanded campaigns */}
                      {expandedSources[source.key] && source.campaigns.map((campaign, ci) => (
                        <tr key={`${source.key}-c-${ci}`} className="bg-gray-50 hover:bg-gray-100 transition-colors">
                          <td className="pl-10 pr-3 py-1 whitespace-normal text-[10px] text-gray-600">
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 bg-gray-400 rounded-full" />
                              <span className="break-words leading-tight min-w-0">{campaign.name}</span>
                            </div>
                          </td>
                          <td className="px-3 py-1 whitespace-nowrap text-[10px] text-gray-600">{fmtCompact(campaign.sessions)}</td>
                          <td className="px-3 py-1 whitespace-nowrap text-[10px] text-gray-600">{campaign.conversions.toLocaleString()}</td>
                          <td className="px-3 py-1 whitespace-nowrap text-[10px] text-gray-600">{campaign.conversion_rate}%</td>
                          <td className="px-3 py-1 whitespace-nowrap text-[10px] text-gray-600">{fmtCompactCur(campaign.revenue)}</td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot className="sticky bottom-0 bg-gray-50 z-10">
                  <tr className={`border-t-2 border-gray-200 cursor-pointer hover:bg-gray-100 transition-colors ${!selectedSource ? 'bg-blue-50' : 'bg-gray-50'}`} onClick={() => setSelectedSource(null)}>
                    <td className="pl-3 pr-3 py-2 whitespace-nowrap">
                      <div className={`font-semibold text-[11px] ${!selectedSource ? 'text-blue-600' : 'text-gray-900'}`}>TOTAL</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className={`font-semibold text-[11px] ${!selectedSource ? 'text-blue-600' : 'text-gray-900'}`}>{fmtCompact(summary.sessions)}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className={`font-semibold text-[11px] ${!selectedSource ? 'text-blue-600' : 'text-gray-900'}`}>{summary.transactions.toLocaleString()}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className={`font-semibold text-[11px] ${!selectedSource ? 'text-blue-600' : 'text-gray-900'}`}>{convRate}%</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <div className={`font-semibold text-[11px] ${!selectedSource ? 'text-blue-600' : 'text-gray-900'}`}>{fmtCompactCur(summary.revenue)}</div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>

        {/* Right: Trend chart + mini charts */}
        <div className="col-span-12 lg:col-span-8 space-y-6">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-800">
                  {selectedSource ? `${selectedSource.source} Trend Analysis` : 'Overall Trend Analysis'}
                </h3>
                <div className="flex items-center gap-3">
                  <select value={primaryMetric} onChange={(e) => setPrimaryMetric(e.target.value)}
                    className="pl-3 pr-8 py-1.5 bg-white border rounded-md text-sm">
                    <option value="revenue">Revenue</option>
                    <option value="sessions">Sessions</option>
                    <option value="conversions">Conversions</option>
                    <option value="cr">CR</option>
                  </select>
                  <select value={secondaryMetric} onChange={(e) => setSecondaryMetric(e.target.value)}
                    className="pl-3 pr-8 py-1.5 bg-white border rounded-md text-sm">
                    <option value="none">Secondary: None</option>
                    <option value="revenue">Secondary: Revenue</option>
                    <option value="sessions">Secondary: Sessions</option>
                    <option value="conversions">Secondary: Conversions</option>
                    <option value="cr">Secondary: CR</option>
                  </select>
                </div>
              </div>
              <div style={{ height: 260 }}>
                {chartData.labels.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData.labels.map((label, i) => {
                      const point = { label };
                      chartData.datasets.forEach(ds => { point[ds.label] = ds.data[i]; });
                      return point;
                    })}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="label" tick={{ fill: '#6b7280', fontSize: 10 }} />
                      <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} />
                      {chartData.datasets.length > 1 && <YAxis yAxisId="right" orientation="right" tick={{ fill: '#6b7280', fontSize: 10 }} />}
                      <Tooltip contentStyle={tooltipStyle} />
                      {chartData.datasets.map((ds, i) => (
                        <Line key={ds.label} type="monotone" dataKey={ds.label} stroke={ds.borderColor}
                          strokeWidth={2} dot={false} yAxisId={i > 0 ? 'right' : undefined} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-sm text-gray-400">No trend data</div>
                )}
              </div>

              {/* Mini monthly charts */}
              {monthlyCharts.labels.length > 0 && (
                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                  {[
                    { title: 'Sessions by Month', data: monthlyCharts.sessions, color: '#3b82f6' },
                    { title: 'Purchases by Month', data: monthlyCharts.purchases, color: '#10b981' },
                    { title: 'Revenue by Month', data: monthlyCharts.revenue, color: '#9333ea', isCurrency: true },
                    { title: 'CR by Month', data: monthlyCharts.cr, color: '#f56565', isPercent: true },
                  ].map(chart => (
                    <div key={chart.title} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      <div className="p-4">
                        <h4 className="font-medium text-gray-800 mb-2">{chart.title}</h4>
                        <div style={{ height: 180 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={monthlyCharts.labels.map((label, i) => ({ label, value: chart.data[i] }))}>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                              <XAxis dataKey="label" tick={{ fill: '#6b7280', fontSize: 10 }} />
                              <YAxis tick={{ fill: '#6b7280', fontSize: 10 }}
                                tickFormatter={v => chart.isCurrency ? `₺${(v/1000).toFixed(0)}K` : chart.isPercent ? `${v}%` : v.toLocaleString()} />
                              <Tooltip contentStyle={tooltipStyle}
                                formatter={v => chart.isCurrency ? fmtCur(v) : chart.isPercent ? `${v}%` : fmtNum(v)} />
                              <Line type="monotone" dataKey="value" stroke={chart.color}
                                fill={chart.color} strokeWidth={2}
                                dot={{ fill: chart.color, strokeWidth: 2, r: 3 }} />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Tab: Devices & Geo ── */
function DevicesGeoTab({ startDate, endDate }) {
  const [devices, setDevices] = useState(null);
  const [geo, setGeo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      apiFetch('devices', startDate, endDate),
      apiFetch('geo', startDate, endDate)
    ])
      .then(([d, g]) => { setDevices(d); setGeo(g); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading) return <Spinner />;

  const catMap = {};
  (devices?.rows || []).forEach(r => {
    const cat = r.device_category || 'unknown';
    catMap[cat] = (catMap[cat] || 0) + Number(r.sessions || 0);
  });
  const devicePie = Object.entries(catMap).map(([name, value]) => ({ name, value }));
  const deviceIcon = { desktop: Monitor, mobile: Smartphone, tablet: Tablet };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b">
            <h3 className="font-semibold text-lg text-gray-800">Device Category</h3>
          </div>
          <div className="p-6">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={devicePie} cx="50%" cy="50%" innerRadius={60} outerRadius={100} dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent*100).toFixed(1)}%)`}>
                  {devicePie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
            <div className="flex justify-center gap-6 mt-2">
              {devicePie.map((d, i) => {
                const DIcon = deviceIcon[d.name.toLowerCase()] || Monitor;
                return (
                  <div key={d.name} className="flex items-center gap-2 text-sm text-gray-500">
                    <DIcon className="w-4 h-4" style={{ color: COLORS[i % COLORS.length] }} />
                    {d.name}: {fmtNum(d.value)}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b">
            <h3 className="font-semibold text-lg text-gray-800">Top Countries</h3>
          </div>
          <div className="p-6">
            <SortableTable
              columns={[
                { key: 'country', label: 'Country' },
                { key: 'sessions', label: 'Sessions', render: v => fmtNum(v) },
                { key: 'users', label: 'Users', render: v => fmtNum(v) },
                { key: 'revenue', label: 'Revenue', render: v => fmtCur(v) },
              ]}
              data={(() => {
                const m = {};
                (geo?.rows || []).forEach(r => {
                  const c = r.country || 'unknown';
                  if (!m[c]) m[c] = { country: c, sessions: 0, users: 0, revenue: 0 };
                  m[c].sessions += Number(r.sessions || 0);
                  m[c].users += Number(r.users || 0);
                  m[c].revenue += Number(r.revenue || 0);
                });
                return Object.values(m).sort((a, b) => b.sessions - a.sessions);
              })()}
            />
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6 border-b">
          <h3 className="font-semibold text-lg text-gray-800">By City</h3>
        </div>
        <div className="p-6">
          <SortableTable
            csvFilename="bq-geo-cities.csv"
            columns={[
              { key: 'country', label: 'Country' },
              { key: 'city', label: 'City' },
              { key: 'sessions', label: 'Sessions', render: v => fmtNum(v) },
              { key: 'users', label: 'Users', render: v => fmtNum(v) },
              { key: 'revenue', label: 'Revenue', render: v => fmtCur(v) },
            ]}
            data={(() => {
              const m = {};
              (geo?.rows || []).forEach(r => {
                const key = `${r.country}|${r.city}`;
                if (!m[key]) m[key] = { country: r.country, city: r.city || '(not set)', sessions: 0, users: 0, revenue: 0 };
                m[key].sessions += Number(r.sessions || 0);
                m[key].users += Number(r.users || 0);
                m[key].revenue += Number(r.revenue || 0);
              });
              return Object.values(m).sort((a, b) => b.sessions - a.sessions);
            })()}
          />
        </div>
      </div>
    </div>
  );
}

/* ── Tab: Pages ── */
function PagesTab({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiFetch('pages', startDate, endDate)
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading) return <Spinner />;
  if (!data?.rows?.length) return <Empty />;

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-6">
        <SortableTable
          csvFilename="bq-pages.csv"
          columns={[
            { key: 'page_path', label: 'Page' },
            { key: 'pageviews', label: 'Pageviews', render: v => fmtNum(v) },
            { key: 'unique_pageviews', label: 'Unique Pageviews', render: v => fmtNum(v) },
            { key: 'entrances', label: 'Entrances', render: v => fmtNum(v) },
            { key: 'exits', label: 'Exits', render: v => fmtNum(v) },
          ]}
          data={data.rows}
        />
      </div>
    </div>
  );
}

/* ── Tab: Ecommerce ── */
function EcommerceTab({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiFetch('ecommerce', startDate, endDate)
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading) return <Spinner />;
  if (!data?.kpi) return <Empty />;

  const { kpi, rows } = data;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard icon={DollarSign} label="Total Revenue" value={fmtCur(kpi.totalRevenue)} variant="primary" />
        <KPICard icon={ShoppingCart} label="Transactions" value={fmtNum(kpi.totalTransactions)} />
        <KPICard icon={Activity} label="Avg. Order Value" value={fmtCur(kpi.aov)} />
        <KPICard icon={Eye} label="Total Items" value={fmtNum(kpi.totalItems)} />
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <SortableTable
            csvFilename="bq-ecommerce.csv"
            columns={[
              { key: 'date', label: 'Date' },
              { key: 'transactionId', label: 'Transaction ID' },
              { key: 'itemQuantity', label: 'Item Quantity', render: v => fmtNum(v) },
              { key: 'revenue', label: 'Revenue', render: v => fmtCur(v) },
              { key: 'uniqueItems', label: 'Unique Items', render: v => fmtNum(v) },
            ]}
            data={rows}
          />
        </div>
      </div>
    </div>
  );
}

/* ── Tab: Banners ── */
function BannersTab({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiFetch('banners', startDate, endDate)
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading) return <Spinner />;
  if (!data?.rows?.length) return <Empty />;

  // CTR'ı satır bazında hesapla (sıralanabilir olması için veriye ekle)
  const rows = (data.rows || []).map(r => {
    const views = Number(r.views || 0);
    const clicks = Number(r.clicks || 0);
    return { ...r, ctr: views > 0 ? clicks / views : 0 };
  });
  const totViews = rows.reduce((s, r) => s + Number(r.views || 0), 0);
  const totClicks = rows.reduce((s, r) => s + Number(r.clicks || 0), 0);
  const totCtr = totViews > 0 ? totClicks / totViews : 0;
  const fmtPct = v => `${(Number(v || 0) * 100).toFixed(2)}%`;
  const isUrl = s => typeof s === 'string' && /^https?:\/\//i.test(s);

  return (
    <div className="space-y-4">
      {/* Özet KPI */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Toplam Görüntülenme', value: fmtNum(totViews) },
          { label: 'Toplam Tıklama', value: fmtNum(totClicks) },
          { label: 'Ortalama CTR', value: fmtPct(totCtr) },
          { label: 'Banner / Creative', value: fmtNum(rows.length) },
        ].map(k => (
          <div key={k.label} className="bg-white border border-gray-200 rounded-xl shadow-sm p-4">
            <p className="text-[11px] text-gray-500">{k.label}</p>
            <p className="text-xl font-bold text-gray-900 mt-1">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <SortableTable
            csvFilename="bq-banners.csv"
            columns={[
              { key: 'promotion_name', label: 'Promotion' },
              { key: 'promotion_id', label: 'Konum' },
              { key: 'platform', label: 'Cihaz' },
              {
                key: 'creative_name', label: 'Creative',
                render: v => isUrl(v)
                  ? <a href={v} target="_blank" rel="noreferrer"><img src={v} alt="creative" loading="lazy" className="h-12 w-auto max-w-[120px] object-contain rounded border border-gray-200 bg-gray-50" /></a>
                  : <span className="text-xs text-gray-400">{v || '—'}</span>,
              },
              { key: 'creative_slot', label: 'Slot' },
              { key: 'views', label: 'Görüntülenme', render: v => fmtNum(v) },
              { key: 'clicks', label: 'Tıklama', render: v => fmtNum(v) },
              { key: 'ctr', label: 'CTR', render: v => fmtPct(v) },
              { key: 'purchases', label: 'Satın Alma', render: v => fmtNum(v) },
              { key: 'revenue', label: 'Gelir', render: v => fmtCur(v) },
            ]}
            data={rows}
          />
        </div>
      </div>
    </div>
  );
}

/* ── Checkout Funnel ── */
const FUNNEL_STEPS = [
  { event: 'view_item',        label: 'View Item',       icon: Eye },
  { event: 'add_to_cart',      label: 'Add to Cart',     icon: ShoppingCart },
  { event: 'begin_checkout',   label: 'Begin Checkout',  icon: ShoppingBag },
  { event: 'purchase',         label: 'Purchase',        icon: ShoppingBag },
];

function buildFunnelSteps(rows) {
  const eventMap = {};
  (rows || []).forEach(r => { eventMap[r.event_name] = Number(r.event_count || 0); });
  return FUNNEL_STEPS.map(s => ({ ...s, count: eventMap[s.event] || 0 }));
}

/* Single funnel card — matches /analytics/funnel style */
function FunnelCard({ title, subtitle, rows, isCurrentMonth }) {
  const steps = buildFunnelSteps(rows);
  const firstCount = steps[0]?.count || 0;
  const purchaseCount = steps[steps.length - 1]?.count || 0;
  const overallConv = firstCount > 0 ? (purchaseCount / firstCount * 100) : 0;

  if (firstCount === 0) return (
    <div className={`rounded-2xl border shadow-sm overflow-hidden ${isCurrentMonth ? 'border-indigo-200 bg-indigo-50/20' : 'border-gray-200 bg-white'}`}>
      <div className="p-5">
        <h4 className="text-sm font-bold text-gray-800">{title}</h4>
        {subtitle && <p className="text-[10px] text-gray-400">{subtitle}</p>}
        <div className="text-center py-10 text-gray-300 text-xs">No funnel data</div>
      </div>
    </div>
  );

  // Build drop-off pairs
  const dropoffPairs = [];
  for (let i = 0; i < steps.length - 1; i++) {
    const fromVal = steps[i].count;
    const toVal = steps[i + 1].count;
    const loss = Math.max(0, fromVal - toVal);
    const dropPct = fromVal > 0 ? (loss / fromVal * 100) : 0;
    dropoffPairs.push({ from: steps[i].label, to: steps[i + 1].label, fromVal, toVal, loss, dropPct });
  }

  return (
    <div className={`rounded-2xl border shadow-sm overflow-hidden ${isCurrentMonth ? 'border-indigo-300 ring-2 ring-indigo-100' : 'border-gray-200 bg-white'}`}>
      {/* Header */}
      <div className={`px-5 py-3 border-b ${isCurrentMonth ? 'bg-indigo-50 border-indigo-100' : 'bg-gray-50 border-gray-100'}`}>
        <div className="flex items-center justify-between">
          <div>
            <h4 className={`text-sm font-bold ${isCurrentMonth ? 'text-indigo-900' : 'text-gray-800'}`}>{title}</h4>
            {subtitle && <p className="text-[10px] text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
          <div className="text-right">
            <p className="text-[10px] text-gray-500">Conversion</p>
            <p className={`text-base font-black ${overallConv >= 0.1 ? 'text-emerald-600' : 'text-red-500'}`}>
              {overallConv.toFixed(2)}%
            </p>
          </div>
        </div>
      </div>

      {/* Funnel bars — /analytics/funnel style */}
      <div className="p-5 space-y-2">
        {steps.map((step, i) => {
          const Icon = step.icon;
          const maxWidth = 100;
          const minWidth = 35;
          const widthStep = (maxWidth - minWidth) / Math.max(steps.length - 1, 1);
          const funnelWidth = Math.max(maxWidth - (i * widthStep), minWidth);
          const prevCount = i > 0 ? steps[i - 1].count : null;
          const stepPassRate = prevCount && prevCount > 0 ? (step.count / prevCount * 100) : null;

          return (
            <div key={step.event} className="relative">
              {/* Drop-off indicator between steps */}
              {i > 0 && prevCount > 0 && (
                <div className="flex items-center justify-center py-0.5 mb-1">
                  <ArrowDownIcon className="w-3 h-3 text-red-300 mr-1" />
                  <span className="text-[10px] text-red-400 font-medium">
                    -{fmtNum(prevCount - step.count)} ({(100 - stepPassRate).toFixed(1)}% drop)
                  </span>
                </div>
              )}
              <div
                className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl p-3 transition-all hover:shadow-md relative overflow-hidden"
                style={{ width: `${funnelWidth}%`, marginLeft: `${(100 - funnelWidth) / 2}%` }}
              >
                <div className="flex items-center justify-between relative z-10">
                  <div className="flex items-center gap-2.5">
                    <div className="p-1.5 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg">
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-[12px] font-semibold text-gray-900 leading-tight">{step.label}</p>
                      <p className="text-[10px] text-gray-500">{step.count.toLocaleString()} events</p>
                    </div>
                  </div>
                  <div className="text-right">
                    {stepPassRate != null ? (
                      <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-bold">
                        {stepPassRate.toFixed(1)}%
                      </span>
                    ) : (
                      <span className="text-[10px] text-gray-400">100%</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Drop-off summary */}
      <div className="px-5 pb-4 pt-1 border-t border-gray-50">
        <div className="space-y-1.5">
          {dropoffPairs.map((pair, i) => (
            <div key={i} className="flex items-center justify-between text-[10px]">
              <span className="text-gray-500">{pair.from} → {pair.to}</span>
              <span className="text-red-500 font-medium">-{pair.dropPct.toFixed(1)}%</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* 3-card monthly funnel comparison */
function MonthlyFunnelComparison() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const brandId = localStorage.getItem('currentBrandId');
    fetch(`${API}/event-counts-monthly`, {
      credentials: 'include',
      headers: { 'X-Brand-ID': brandId || '' },
    })
      .then(r => r.json())
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center py-10">
      <Loader2 className="w-5 h-5 text-indigo-400 animate-spin" />
    </div>
  );

  const months = data?.months || [];
  if (months.length === 0) return null;

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <h3 className="font-semibold text-gray-800">Monthly Funnel Comparison</h3>
        <span className="text-[10px] text-gray-400">Last 3 months side-by-side</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {months.map((m, i) => (
          <FunnelCard
            key={m.month}
            title={m.label}
            subtitle={`${m.start} — ${m.end}`}
            rows={m.rows}
            isCurrentMonth={i === 0}
          />
        ))}
      </div>
    </div>
  );
}

/* Full-width checkout funnel (for current date range selection) */
function CheckoutFunnel({ rows }) {
  const steps = buildFunnelSteps(rows);
  if (steps.length < 2) return null;

  const maxCount = steps[0]?.count || 1;
  const firstCount = steps[0]?.count || 1;

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-semibold text-gray-800">Checkout Funnel</h3>
            <p className="text-xs text-gray-500 mt-0.5">GA4 event-based conversion funnel (selected period)</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">Overall conversion:</span>
            <span className="text-sm font-bold text-emerald-600">
              {firstCount > 0 ? ((steps[steps.length - 1].count / firstCount) * 100).toFixed(2) : 0}%
            </span>
          </div>
        </div>

        <div className="space-y-0">
          {steps.map((step, i) => {
            const Icon = step.icon;
            const widthPct = maxCount > 0 ? Math.max((step.count / maxCount) * 100, 8) : 8;
            const prevCount = i > 0 ? steps[i - 1].count : null;
            const dropPct = prevCount && prevCount > 0 ? ((1 - step.count / prevCount) * 100) : null;
            const fromFirstPct = firstCount > 0 ? ((step.count / firstCount) * 100) : 0;

            return (
              <React.Fragment key={step.event}>
                {i > 0 && prevCount > 0 && (
                  <div className="flex items-center py-1 pl-8">
                    <ArrowDownIcon className="w-3.5 h-3.5 text-red-400 mr-2 flex-shrink-0" />
                    <span className="text-[10px] font-medium text-red-400">
                      -{fmtNum(prevCount - step.count)} ({dropPct.toFixed(1)}% drop)
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-3 group">
                  <div className="flex items-center gap-2 w-[140px] flex-shrink-0">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: step.color + '18' }}>
                      <Icon className="w-3.5 h-3.5" style={{ color: step.color }} />
                    </div>
                    <span className="text-xs font-medium text-gray-700 truncate">{step.label}</span>
                  </div>
                  <div className="flex-1 relative">
                    <div className="h-9 rounded-lg relative overflow-hidden bg-gray-50">
                      <div className="h-full rounded-lg transition-all duration-700 ease-out flex items-center"
                        style={{ width: `${widthPct}%`, backgroundColor: step.color + '20', borderLeft: `3px solid ${step.color}` }}>
                        <span className="text-xs font-bold text-gray-900 pl-3 whitespace-nowrap">{fmtNum(step.count)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="w-[70px] text-right flex-shrink-0">
                    <span className="text-xs font-semibold tabular-nums" style={{ color: step.color }}>
                      {fromFirstPct.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>

        <div className="mt-6 pt-4 border-t border-gray-100">
          <h4 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-3">Step Conversion Rates</h4>
          <div className="flex flex-wrap gap-2">
            {steps.map((step, i) => {
              if (i === 0) return null;
              const prev = steps[i - 1];
              const rate = prev.count > 0 ? ((step.count / prev.count) * 100) : 0;
              return (
                <div key={step.event} className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-2.5 py-1.5">
                  <span className="text-[10px] text-gray-500 truncate max-w-[60px]">{prev.label}</span>
                  <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                  <span className="text-[10px] text-gray-500 truncate max-w-[60px]">{step.label}</span>
                  <span className={`text-[11px] font-bold ml-1 ${rate >= 50 ? 'text-emerald-600' : rate >= 20 ? 'text-amber-600' : 'text-red-500'}`}>
                    {rate.toFixed(1)}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Tab: Events ── */
function EventsTab({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiFetch('event-counts', startDate, endDate)
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading) return <Spinner />;
  if (!data?.rows?.length) return <Empty />;

  const { rows, timeseries } = data;

  const top10Names = rows.slice(0, 10).map(r => r.event_name);
  const chartMap = {};
  (timeseries || []).forEach(r => {
    const d = String(r.date).slice(0, 10);
    if (!chartMap[d]) chartMap[d] = { date: d };
    chartMap[d][r.event_name] = Number(r.event_count || 0);
  });
  const chartData = Object.values(chartMap).sort((a, b) => a.date.localeCompare(b.date));

  const totalEvents = rows.reduce((s, r) => s + Number(r.event_count || 0), 0);

  const pieData = rows.slice(0, 10).map(r => ({
    name: r.event_name,
    value: Number(r.event_count || 0)
  }));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard icon={Zap} label="Total Events" value={fmtNum(totalEvents)} variant="primary" />
        <KPICard icon={Activity} label="Unique Event Types" value={fmtNum(rows.length)} />
        <KPICard icon={TrendingUp} label="Top Event" value={rows[0]?.event_name || '-'} />
        <KPICard icon={Eye} label="Top Event Count" value={fmtNum(rows[0]?.event_count)} />
      </div>

      {/* Monthly Funnel Comparison — 3 cards */}
      <MonthlyFunnelComparison />

      {/* Checkout Funnel (selected date range) */}
      <CheckoutFunnel rows={rows} />

      {/* Timeseries Chart */}
      {chartData.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6">
            <h3 className="font-semibold text-gray-800 mb-4">Top 10 Events Over Time</h3>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend />
                {top10Names.map((name, i) => (
                  <Line key={name} type="monotone" dataKey={name}
                    stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6">
            <h3 className="font-semibold text-gray-800 mb-4">Event Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                  outerRadius={100} label={({ name, percent }) => `${name} (${(percent * 100).toFixed(1)}%)`}
                  labelLine={{ strokeWidth: 1 }}>
                  {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="p-6">
            <SortableTable
              csvFilename="bq-event-counts.csv"
              columns={[
                { key: 'event_name', label: 'Event Name' },
                { key: 'event_count', label: 'Count', render: v => fmtNum(v) },
              ]}
              data={rows}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Helpers ── */
function Spinner() {
  return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
    </div>
  );
}

function Empty() {
  return (
    <div className="text-center py-20 text-gray-400">
      <BarChart3 className="w-12 h-12 mx-auto mb-3 opacity-30" />
      <p className="text-sm">No data found for this period.</p>
    </div>
  );
}

/* ── Channel logo helper ── */
const CHANNEL_LOGOS = {
  'Google Ads': '/google_ads-512.webp',
  'Meta Ads': '/6033716.png',
  'Criteo': '/CRcriteopng.png',
  'RTB House': '/RTBhouseiconPNG.png',
  'Pinterest': '/pinterestlogopng.png',
};
function ChannelLabel({ name }) {
  const logo = CHANNEL_LOGOS[name];
  return (
    <span className="inline-flex items-center gap-1.5">
      {logo && <img src={logo} alt="" className="w-4 h-4 flex-shrink-0" />}
      {name}
    </span>
  );
}

/* ── Monthly Source Revenue Table (Overview2 style) ── */
function MonthlySourceTable({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [spendData, setSpendData] = useState({ byMonthChannel: {}, channelTotals: {} });
  const [loading, setLoading] = useState(true);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [metric, setMetric] = useState('revenue');

  useEffect(() => {
    setLoading(true);
    const brandId = localStorage.getItem('currentBrandId');
    Promise.all([
      apiFetch('monthly-sources', startDate, endDate),
      fetch(`https://api.glowytics.com/api/google-ads/unified-campaign-performance-monthly?dateRange=custom&startDate=${startDate}&endDate=${endDate}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' }
      }).then(r => r.ok ? r.json() : []).catch(() => [])
    ]).then(([bqData, spendRows]) => {
      setData(bqData);
      const toMonthKey = (v) => {
        if (!v) return '';
        const d = new Date(v);
        if (isNaN(d)) return '';
        d.setUTCHours(d.getUTCHours() + 12);
        return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      };
      const byMC = {};
      const chTotals = {};
      for (const row of spendRows) {
        const mk = toMonthKey(row.month);
        if (!mk) continue;
        const ch = row.channel || 'Other';
        const cost = Number(row.google_cost || 0) + Number(row.meta_cost || 0) + Number(row.criteo_cost || 0) + Number(row.rtb_cost || 0) + Number(row.pinterest_cost || 0);
        if (!byMC[mk]) byMC[mk] = {};
        byMC[mk][ch] = (byMC[mk][ch] || 0) + cost;
        chTotals[ch] = (chTotals[ch] || 0) + cost;
      }
      setSpendData({ byMonthChannel: byMC, channelTotals: chTotals });
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading) return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
      </div>
    </div>
  );

  if (!data?.rows?.length) return null;

  const { headers, rows, totals } = data;
  const channels = headers.filter(h => h !== 'Month' && h !== 'Total');

  const formatMonth = (m) => {
    const [y, mo] = m.split('-');
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${names[parseInt(mo) - 1]} ${y}`;
  };

  const getCost = (month, ch) => spendData.byMonthChannel[month]?.[ch] || 0;
  const getMonthTotalCost = (month) => {
    const mc = spendData.byMonthChannel[month];
    return mc ? Object.values(mc).reduce((s, v) => s + v, 0) : 0;
  };
  const grandTotalCost = Object.values(spendData.channelTotals).reduce((s, v) => s + v, 0);

  // Collect all channels that have data across any month
  const activeChannels = channels.filter(ch =>
    rows.some(row => (row[ch]?.revenue || 0) > 0 || (row[ch]?.sessions || 0) > 0 || getCost(row.month, ch) > 0)
  );

  // Build pivot data: each row = month, columns = channels
  const monthRows = rows.map(row => {
    const totalData = row['Total'] || {};
    const monthTotalCost = getMonthTotalCost(row.month);
    const channelData = {};
    for (const ch of activeChannels) {
      const d = row[ch] || {};
      channelData[ch] = {
        revenue: d.revenue || 0,
        sessions: d.sessions || 0,
        transactions: d.transactions || 0,
        cost: getCost(row.month, ch),
      };
    }
    return {
      month: row.month, label: formatMonth(row.month),
      total: { revenue: totalData.revenue || 0, sessions: totalData.sessions || 0, transactions: totalData.transactions || 0, cost: monthTotalCost },
      channels: channelData,
    };
  });

  // Channel totals for footer
  const channelFooter = {};
  for (const ch of activeChannels) {
    channelFooter[ch] = { revenue: 0, sessions: 0, transactions: 0, cost: 0 };
    for (const row of rows) {
      const d = row[ch] || {};
      channelFooter[ch].revenue += d.revenue || 0;
      channelFooter[ch].sessions += d.sessions || 0;
      channelFooter[ch].transactions += d.transactions || 0;
      channelFooter[ch].cost += getCost(row.month, ch);
    }
  }
  const grandTotal = {
    revenue: totals['Total']?.revenue || 0,
    sessions: totals['Total']?.sessions || 0,
    transactions: totals['Total']?.transactions || 0,
    cost: grandTotalCost,
  };

  const metricOptions = [
    { value: 'revenue', label: 'Revenue' },
    { value: 'sessions', label: 'Sessions' },
    { value: 'transactions', label: 'Transactions' },
    { value: 'cr', label: 'Conv. Rate' },
  ];

  const fmtMetric = (d) => {
    if (metric === 'revenue') return d.revenue > 0 ? fmtCur(d.revenue) : '-';
    if (metric === 'sessions') return d.sessions > 0 ? fmtNum(d.sessions) : '-';
    if (metric === 'transactions') return d.transactions > 0 ? fmtNum(d.transactions) : '-';
    if (metric === 'cr') return d.sessions > 0 ? ((d.transactions / d.sessions) * 100).toFixed(2) + '%' : '-';
    return '-';
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-6 border-b flex items-center justify-between">
        <h3 className="font-semibold text-lg text-gray-800">Monthly Channel Performance <span className="text-xs font-normal text-blue-500 ml-1">(BigQuery)</span></h3>
        <select
          value={metric}
          onChange={e => setMetric(e.target.value)}
          className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {metricOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto" style={{ maxHeight: 600 }}>
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="bg-gray-50 text-left">
              <th className="px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap sticky left-0 bg-gray-50 z-20">Month</th>
              {activeChannels.map(ch => (
                <th key={ch} className="px-4 py-3 text-xs font-medium text-gray-500 text-right whitespace-nowrap">
                  <ChannelLabel name={ch} />
                </th>
              ))}
              <th className="px-4 py-3 text-xs font-medium text-gray-500 text-right whitespace-nowrap">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {monthRows.map((row) => (
              <tr key={row.month} className="bg-white hover:bg-gray-50">
                <td className="px-4 py-2 font-medium text-gray-800 whitespace-nowrap sticky left-0 bg-white z-10">{row.label}</td>
                {activeChannels.map(ch => {
                  const d = row.channels[ch];
                  return (
                    <td key={ch} className="px-4 py-2 text-right">
                      <div className="text-gray-800">{fmtMetric(d)}</div>
                      {d.cost > 0 && <div className="text-[10px] text-red-400 leading-tight">cost: {fmtCur(d.cost)}</div>}
                    </td>
                  );
                })}
                <td className="px-4 py-2 text-right">
                  <div className="font-medium text-green-700">{fmtMetric(row.total)}</div>
                  {row.total.cost > 0 && <div className="text-[10px] text-red-400 leading-tight">cost: {fmtCur(row.total.cost)}</div>}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="sticky bottom-0 bg-white z-10">
            <tr className="border-t-2 border-gray-200">
              <td className="px-4 py-3 font-semibold text-gray-900 sticky left-0 bg-white z-20">TOTAL</td>
              {activeChannels.map(ch => {
                const f = channelFooter[ch];
                return (
                  <td key={ch} className="px-4 py-3 text-right">
                    <div className="font-semibold text-gray-800">{fmtMetric(f)}</div>
                    {f.cost > 0 && <div className="text-[10px] text-red-400 leading-tight">cost: {fmtCur(f.cost)}</div>}
                  </td>
                );
              })}
              <td className="px-4 py-3 text-right">
                <div className="font-semibold text-green-700">{fmtMetric(grandTotal)}</div>
                {grandTotalCost > 0 && <div className="text-[10px] text-red-400 leading-tight">cost: {fmtCur(grandTotalCost)}</div>}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ── Weekly Source Revenue Table (week-bucketed twin of MonthlySourceTable) ── */
function WeeklySourceTable({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [spendData, setSpendData] = useState({ byWeekChannel: {}, channelTotals: {} });
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState('revenue');

  useEffect(() => {
    setLoading(true);
    const brandId = localStorage.getItem('currentBrandId');
    Promise.all([
      apiFetch('weekly-sources', startDate, endDate),
      fetch(`https://api.glowytics.com/api/google-ads/unified-campaign-performance-weekly?dateRange=custom&startDate=${startDate}&endDate=${endDate}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' }
      }).then(r => r.ok ? r.json() : []).catch(() => [])
    ]).then(([bqData, spendRows]) => {
      setData(bqData);
      // Spend feed returns rows with `week` as a Date/ISO string at week-start.
      // Normalize to YYYY-MM-DD so it matches the BQ bucket key.
      const toWeekKey = (v) => {
        if (!v) return '';
        if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return v.slice(0, 10);
        const d = new Date(v);
        if (isNaN(d)) return '';
        return d.toISOString().slice(0, 10);
      };
      const byWC = {};
      const chTotals = {};
      for (const row of (Array.isArray(spendRows) ? spendRows : [])) {
        const wk = toWeekKey(row.week);
        if (!wk) continue;
        const ch = row.channel || 'Other';
        const cost = Number(row.google_cost || 0) + Number(row.meta_cost || 0) + Number(row.criteo_cost || 0) + Number(row.rtb_cost || 0) + Number(row.pinterest_cost || 0);
        if (!byWC[wk]) byWC[wk] = {};
        byWC[wk][ch] = (byWC[wk][ch] || 0) + cost;
        chTotals[ch] = (chTotals[ch] || 0) + cost;
      }
      setSpendData({ byWeekChannel: byWC, channelTotals: chTotals });
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading) return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
      </div>
    </div>
  );

  if (!data?.rows?.length) return null;

  const { headers, rows, totals } = data;
  const channels = headers.filter(h => h !== 'Week' && h !== 'Total');

  const formatWeek = (wk) => {
    const s = new Date(wk + 'T00:00:00');
    if (isNaN(s)) return wk;
    const e = new Date(s); e.setDate(e.getDate() + 6);
    const f = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${f(s)} – ${f(e)} ${e.getFullYear()}`;
  };

  const getCost = (week, ch) => spendData.byWeekChannel[week]?.[ch] || 0;
  const getWeekTotalCost = (week) => {
    const wc = spendData.byWeekChannel[week];
    return wc ? Object.values(wc).reduce((s, v) => s + v, 0) : 0;
  };
  const grandTotalCost = Object.values(spendData.channelTotals).reduce((s, v) => s + v, 0);

  const activeChannels = channels.filter(ch =>
    rows.some(row => (row[ch]?.revenue || 0) > 0 || (row[ch]?.sessions || 0) > 0 || getCost(row.week, ch) > 0)
  );

  const weekRows = rows.map(row => {
    const totalData = row['Total'] || {};
    const weekTotalCost = getWeekTotalCost(row.week);
    const channelData = {};
    for (const ch of activeChannels) {
      const d = row[ch] || {};
      channelData[ch] = {
        revenue: d.revenue || 0,
        sessions: d.sessions || 0,
        transactions: d.transactions || 0,
        cost: getCost(row.week, ch),
      };
    }
    return {
      week: row.week, label: formatWeek(row.week),
      total: { revenue: totalData.revenue || 0, sessions: totalData.sessions || 0, transactions: totalData.transactions || 0, cost: weekTotalCost },
      channels: channelData,
    };
  });

  const channelFooter = {};
  for (const ch of activeChannels) {
    channelFooter[ch] = { revenue: 0, sessions: 0, transactions: 0, cost: 0 };
    for (const row of rows) {
      const d = row[ch] || {};
      channelFooter[ch].revenue += d.revenue || 0;
      channelFooter[ch].sessions += d.sessions || 0;
      channelFooter[ch].transactions += d.transactions || 0;
      channelFooter[ch].cost += getCost(row.week, ch);
    }
  }
  const grandTotal = {
    revenue: totals['Total']?.revenue || 0,
    sessions: totals['Total']?.sessions || 0,
    transactions: totals['Total']?.transactions || 0,
    cost: grandTotalCost,
  };

  const metricOptions = [
    { value: 'revenue', label: 'Revenue' },
    { value: 'sessions', label: 'Sessions' },
    { value: 'transactions', label: 'Transactions' },
    { value: 'cr', label: 'Conv. Rate' },
    { value: 'aov', label: 'Avg Order Value' },
  ];

  const fmtMetric = (d) => {
    if (metric === 'revenue') return d.revenue > 0 ? fmtCur(d.revenue) : '-';
    if (metric === 'sessions') return d.sessions > 0 ? fmtNum(d.sessions) : '-';
    if (metric === 'transactions') return d.transactions > 0 ? fmtNum(d.transactions) : '-';
    if (metric === 'cr') return d.sessions > 0 ? ((d.transactions / d.sessions) * 100).toFixed(2) + '%' : '-';
    // AOV is recomputed from the sums at every level (cell / TOTAL column / TOTAL row),
    // so the totals stay revenue-weighted instead of averaging the averages.
    if (metric === 'aov') return d.transactions > 0 ? fmtCur(d.revenue / d.transactions) : '-';
    return '-';
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-6 border-b flex items-center justify-between">
        <h3 className="font-semibold text-lg text-gray-800">Weekly Channel Performance <span className="text-xs font-normal text-blue-500 ml-1">(BigQuery)</span></h3>
        <select
          value={metric}
          onChange={e => setMetric(e.target.value)}
          className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {metricOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto" style={{ maxHeight: 600 }}>
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="bg-gray-50 text-left">
              <th className="px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap sticky left-0 bg-gray-50 z-20">Week</th>
              {activeChannels.map(ch => (
                <th key={ch} className="px-4 py-3 text-xs font-medium text-gray-500 text-right whitespace-nowrap">
                  <ChannelLabel name={ch} />
                </th>
              ))}
              <th className="px-4 py-3 text-xs font-medium text-gray-500 text-right whitespace-nowrap">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {weekRows.map((row) => (
              <tr key={row.week} className="bg-white hover:bg-gray-50">
                <td className="px-4 py-2 font-medium text-gray-800 whitespace-nowrap sticky left-0 bg-white z-10">{row.label}</td>
                {activeChannels.map(ch => {
                  const d = row.channels[ch];
                  return (
                    <td key={ch} className="px-4 py-2 text-right">
                      <div className="text-gray-800">{fmtMetric(d)}</div>
                      {d.cost > 0 && <div className="text-[10px] text-red-400 leading-tight">cost: {fmtCur(d.cost)}</div>}
                    </td>
                  );
                })}
                <td className="px-4 py-2 text-right">
                  <div className="font-medium text-green-700">{fmtMetric(row.total)}</div>
                  {row.total.cost > 0 && <div className="text-[10px] text-red-400 leading-tight">cost: {fmtCur(row.total.cost)}</div>}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="sticky bottom-0 bg-white z-10">
            <tr className="border-t-2 border-gray-200">
              <td className="px-4 py-3 font-semibold text-gray-900 sticky left-0 bg-white z-20">TOTAL</td>
              {activeChannels.map(ch => {
                const f = channelFooter[ch];
                return (
                  <td key={ch} className="px-4 py-3 text-right">
                    <div className="font-semibold text-gray-800">{fmtMetric(f)}</div>
                    {f.cost > 0 && <div className="text-[10px] text-red-400 leading-tight">cost: {fmtCur(f.cost)}</div>}
                  </td>
                );
              })}
              <td className="px-4 py-3 text-right">
                <div className="font-semibold text-green-700">{fmtMetric(grandTotal)}</div>
                {grandTotalCost > 0 && <div className="text-[10px] text-red-400 leading-tight">cost: {fmtCur(grandTotalCost)}</div>}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ── Monthly Source Revenue Table (Without Cost Lines) ── */
function MonthlySourceTableNoCost({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [spendData, setSpendData] = useState({ byMonthChannel: {}, channelTotals: {} });
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState('revenue');

  useEffect(() => {
    setLoading(true);
    const brandId = localStorage.getItem('currentBrandId');
    Promise.all([
      apiFetch('monthly-sources', startDate, endDate),
      fetch(`https://api.glowytics.com/api/google-ads/unified-campaign-performance-monthly?dateRange=custom&startDate=${startDate}&endDate=${endDate}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' }
      }).then(r => r.ok ? r.json() : []).catch(() => [])
    ]).then(([bqData, spendRows]) => {
      setData(bqData);
      const toMonthKey = (v) => {
        if (!v) return '';
        const d = new Date(v);
        if (isNaN(d)) return '';
        d.setUTCHours(d.getUTCHours() + 12);
        return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      };
      const byMC = {};
      const chTotals = {};
      for (const row of spendRows) {
        const mk = toMonthKey(row.month);
        if (!mk) continue;
        const ch = row.channel || 'Other';
        const cost = Number(row.google_cost || 0) + Number(row.meta_cost || 0) + Number(row.criteo_cost || 0) + Number(row.rtb_cost || 0) + Number(row.pinterest_cost || 0);
        if (!byMC[mk]) byMC[mk] = {};
        byMC[mk][ch] = (byMC[mk][ch] || 0) + cost;
        chTotals[ch] = (chTotals[ch] || 0) + cost;
      }
      setSpendData({ byMonthChannel: byMC, channelTotals: chTotals });
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading) return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
      </div>
    </div>
  );

  if (!data?.rows?.length) return null;

  const { headers, rows, totals } = data;
  const channels = headers.filter(h => h !== 'Month' && h !== 'Total');

  const formatMonth = (m) => {
    const [y, mo] = m.split('-');
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${names[parseInt(mo) - 1]} ${y}`;
  };

  const activeChannels = channels.filter(ch =>
    rows.some(row => (row[ch]?.revenue || 0) > 0 || (row[ch]?.sessions || 0) > 0)
  );

  const monthRows = rows.map(row => {
    const totalData = row['Total'] || {};
    const channelData = {};
    for (const ch of activeChannels) {
      const d = row[ch] || {};
      channelData[ch] = {
        revenue: d.revenue || 0,
        sessions: d.sessions || 0,
        transactions: d.transactions || 0,
      };
    }
    return {
      month: row.month, label: formatMonth(row.month),
      total: { revenue: totalData.revenue || 0, sessions: totalData.sessions || 0, transactions: totalData.transactions || 0 },
      channels: channelData,
    };
  });

  const channelFooter = {};
  for (const ch of activeChannels) {
    channelFooter[ch] = { revenue: 0, sessions: 0, transactions: 0 };
    for (const row of rows) {
      const d = row[ch] || {};
      channelFooter[ch].revenue += d.revenue || 0;
      channelFooter[ch].sessions += d.sessions || 0;
      channelFooter[ch].transactions += d.transactions || 0;
    }
  }
  const grandTotal = {
    revenue: totals['Total']?.revenue || 0,
    sessions: totals['Total']?.sessions || 0,
    transactions: totals['Total']?.transactions || 0,
  };

  const metricOptions = [
    { value: 'revenue', label: 'Revenue' },
    { value: 'sessions', label: 'Sessions' },
    { value: 'transactions', label: 'Transactions' },
    { value: 'cr', label: 'Conv. Rate' },
  ];

  const fmtMetric = (d) => {
    if (metric === 'revenue') return d.revenue > 0 ? fmtCur(d.revenue) : '-';
    if (metric === 'sessions') return d.sessions > 0 ? fmtNum(d.sessions) : '-';
    if (metric === 'transactions') return d.transactions > 0 ? fmtNum(d.transactions) : '-';
    if (metric === 'cr') return d.sessions > 0 ? ((d.transactions / d.sessions) * 100).toFixed(2) + '%' : '-';
    return '-';
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-6 border-b flex items-center justify-between">
        <h3 className="font-semibold text-lg text-gray-800">Monthly Channel Performance — Without Cost <span className="text-xs font-normal text-emerald-500 ml-1">(BigQuery)</span></h3>
        <select
          value={metric}
          onChange={e => setMetric(e.target.value)}
          className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
        >
          {metricOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div className="overflow-x-auto" style={{ maxHeight: 600 }}>
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="bg-gray-50 text-left">
              <th className="px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap sticky left-0 bg-gray-50 z-20">Month</th>
              {activeChannels.map(ch => (
                <th key={ch} className="px-4 py-3 text-xs font-medium text-gray-500 text-right whitespace-nowrap">
                  <ChannelLabel name={ch} />
                </th>
              ))}
              <th className="px-4 py-3 text-xs font-medium text-gray-500 text-right whitespace-nowrap">Total</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {monthRows.map((row) => (
              <tr key={row.month} className="bg-white hover:bg-gray-50">
                <td className="px-4 py-2 font-medium text-gray-800 whitespace-nowrap sticky left-0 bg-white z-10">{row.label}</td>
                {activeChannels.map(ch => {
                  const d = row.channels[ch];
                  return (
                    <td key={ch} className="px-4 py-2 text-right">
                      <div className="text-gray-800">{fmtMetric(d)}</div>
                    </td>
                  );
                })}
                <td className="px-4 py-2 text-right">
                  <div className="font-medium text-green-700">{fmtMetric(row.total)}</div>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="sticky bottom-0 bg-white z-10">
            <tr className="border-t-2 border-gray-200">
              <td className="px-4 py-3 font-semibold text-gray-900 sticky left-0 bg-white z-20">TOTAL</td>
              {activeChannels.map(ch => {
                const f = channelFooter[ch];
                return (
                  <td key={ch} className="px-4 py-3 text-right">
                    <div className="font-semibold text-gray-800">{fmtMetric(f)}</div>
                  </td>
                );
              })}
              <td className="px-4 py-3 text-right">
                <div className="font-semibold text-green-700">{fmtMetric(grandTotal)}</div>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ── Daily Channel Performance (BigQuery, with cost) — collapsible ── */
function DailySourceTable({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [spendData, setSpendData] = useState({ byDateChannel: {}, channelTotals: {} });
  const [loading, setLoading] = useState(false);
  const [metric, setMetric] = useState('revenue');
  const [open, setOpen] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    setFetched(false);
    if (open) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const fetchData = () => {
    setLoading(true);
    const brandId = localStorage.getItem('currentBrandId');
    Promise.all([
      apiFetch('daily-sources', startDate, endDate),
      fetch(`https://api.glowytics.com/api/google-ads/unified-campaign-performance?dateRange=custom&startDate=${startDate}&endDate=${endDate}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' }
      }).then(r => r.ok ? r.json() : []).catch(() => [])
    ]).then(([bqData, spendRows]) => {
      setData(bqData);
      const toDateKey = (v) => {
        if (!v) return '';
        const d = new Date(v);
        if (isNaN(d)) return '';
        return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
      };
      const byDC = {};
      const chTotals = {};
      for (const row of spendRows) {
        const dk = toDateKey(row.date);
        if (!dk) continue;
        const ch = row.channel || 'Other';
        const cost = Number(row.google_cost || 0) + Number(row.meta_cost || 0) + Number(row.criteo_cost || 0) + Number(row.rtb_cost || 0) + Number(row.pinterest_cost || 0);
        if (!byDC[dk]) byDC[dk] = {};
        byDC[dk][ch] = (byDC[dk][ch] || 0) + cost;
        chTotals[ch] = (chTotals[ch] || 0) + cost;
      }
      setSpendData({ byDateChannel: byDC, channelTotals: chTotals });
      setFetched(true);
    }).catch(console.error)
      .finally(() => setLoading(false));
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && !fetched) fetchData();
  };

  const formatDate = (d) => {
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dowNames = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
    const dow = (dt.getUTCDay() + 6) % 7;
    return `${dt.getUTCDate()} ${names[dt.getUTCMonth()]} ${dt.getUTCFullYear()} · ${dowNames[dow]}`;
  };

  const toDateKey = (v) => {
    if (!v) return '';
    const d = new Date(v);
    if (isNaN(d)) return '';
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
  };

  const metricOptions = [
    { value: 'revenue', label: 'Revenue' },
    { value: 'sessions', label: 'Sessions' },
    { value: 'transactions', label: 'Transactions' },
    { value: 'cr', label: 'Conv. Rate' },
  ];

  const fmtMetric = (d) => {
    if (!d) return '-';
    if (metric === 'revenue') return d.revenue > 0 ? fmtCur(d.revenue) : '-';
    if (metric === 'sessions') return d.sessions > 0 ? fmtNum(d.sessions) : '-';
    if (metric === 'transactions') return d.transactions > 0 ? fmtNum(d.transactions) : '-';
    if (metric === 'cr') return d.sessions > 0 ? ((d.transactions / d.sessions) * 100).toFixed(2) + '%' : '-';
    return '-';
  };

  const rows = data?.rows || [];
  const channels = (data?.headers || []).filter(h => h !== 'Date' && h !== 'Total');

  const getCost = (date, ch) => spendData.byDateChannel[toDateKey(date)]?.[ch] || 0;
  const getDateTotalCost = (date) => {
    const dc = spendData.byDateChannel[toDateKey(date)];
    return dc ? Object.values(dc).reduce((s, v) => s + v, 0) : 0;
  };
  const grandTotalCost = Object.values(spendData.channelTotals).reduce((s, v) => s + v, 0);

  const activeChannels = channels.filter(ch =>
    rows.some(row => (row[ch]?.revenue || 0) > 0 || (row[ch]?.sessions || 0) > 0 || getCost(row.date, ch) > 0)
  );
  // Show most recent dates first so "last 3 days" are at the top
  const displayRows = [...rows].reverse();

  const channelFooter = {};
  for (const ch of activeChannels) {
    channelFooter[ch] = { revenue: 0, sessions: 0, transactions: 0, cost: 0 };
    for (const row of rows) {
      const d = row[ch] || {};
      channelFooter[ch].revenue += d.revenue || 0;
      channelFooter[ch].sessions += d.sessions || 0;
      channelFooter[ch].transactions += d.transactions || 0;
      channelFooter[ch].cost += getCost(row.date, ch);
    }
  }
  const grandTotal = {
    revenue: data?.totals?.['Total']?.revenue || 0,
    sessions: data?.totals?.['Total']?.sessions || 0,
    transactions: data?.totals?.['Total']?.transactions || 0,
    cost: grandTotalCost,
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={toggleOpen}
        className="w-full p-6 border-b flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={18} className="text-gray-500" /> : <ChevronRight size={18} className="text-gray-500" />}
          <h3 className="font-semibold text-lg text-gray-800 text-left">
            Daily Channel Performance
            <span className="text-xs font-normal text-blue-500 ml-1">(BigQuery)</span>
          </h3>
        </div>
        <span className="text-xs text-gray-500">{open ? 'Click to collapse' : 'Click to expand'}</span>
      </button>

      {open && (
        <>
          <div className="px-6 py-3 border-b flex items-center justify-end">
            <select
              value={metric}
              onChange={e => setMetric(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {metricOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : !rows.length ? (
            <div className="py-12 text-center text-gray-500 text-sm">No data available for the selected range.</div>
          ) : (
            <div className="overflow-x-auto" style={{ maxHeight: 600 }}>
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap sticky left-0 bg-gray-50 z-20">Date</th>
                    {activeChannels.map(ch => (
                      <th key={ch} className="px-4 py-3 text-xs font-medium text-gray-500 text-right whitespace-nowrap">
                        <ChannelLabel name={ch} />
                      </th>
                    ))}
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 text-right whitespace-nowrap">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {displayRows.map((row) => {
                    const totalData = row['Total'] || {};
                    const dateTotalCost = getDateTotalCost(row.date);
                    return (
                      <tr key={row.date} className="bg-white hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium text-gray-800 whitespace-nowrap sticky left-0 bg-white z-10">{formatDate(row.date)}</td>
                        {activeChannels.map(ch => {
                          const d = row[ch] || { revenue: 0, sessions: 0, transactions: 0 };
                          const cost = getCost(row.date, ch);
                          return (
                            <td key={ch} className="px-4 py-2 text-right">
                              <div className="text-gray-800">{fmtMetric(d)}</div>
                              {cost > 0 && <div className="text-[10px] text-red-400 leading-tight">cost: {fmtCur(cost)}</div>}
                            </td>
                          );
                        })}
                        <td className="px-4 py-2 text-right">
                          <div className="font-medium text-green-700">{fmtMetric(totalData)}</div>
                          {dateTotalCost > 0 && <div className="text-[10px] text-red-400 leading-tight">cost: {fmtCur(dateTotalCost)}</div>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="sticky bottom-0 bg-white z-10">
                  <tr className="border-t-2 border-gray-200">
                    <td className="px-4 py-3 font-semibold text-gray-900 sticky left-0 bg-white z-20">TOTAL</td>
                    {activeChannels.map(ch => {
                      const f = channelFooter[ch];
                      return (
                        <td key={ch} className="px-4 py-3 text-right">
                          <div className="font-semibold text-gray-800">{fmtMetric(f)}</div>
                          {f.cost > 0 && <div className="text-[10px] text-red-400 leading-tight">cost: {fmtCur(f.cost)}</div>}
                        </td>
                      );
                    })}
                    <td className="px-4 py-3 text-right">
                      <div className="font-semibold text-green-700">{fmtMetric(grandTotal)}</div>
                      {grandTotalCost > 0 && <div className="text-[10px] text-red-400 leading-tight">cost: {fmtCur(grandTotalCost)}</div>}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Daily Channel Performance — Without Cost (BigQuery) — collapsible ── */
function DailySourceTableNoCost({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [metric, setMetric] = useState('revenue');
  const [open, setOpen] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    // refetch whenever range changes if the panel is already open
    setFetched(false);
    if (open) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const fetchData = () => {
    setLoading(true);
    apiFetch('daily-sources', startDate, endDate)
      .then(d => { setData(d); setFetched(true); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && !fetched) fetchData();
  };

  const formatDate = (d) => {
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dowNames = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
    const dow = (dt.getUTCDay() + 6) % 7;
    return `${dt.getUTCDate()} ${names[dt.getUTCMonth()]} ${dt.getUTCFullYear()} · ${dowNames[dow]}`;
  };

  const metricOptions = [
    { value: 'revenue', label: 'Revenue' },
    { value: 'sessions', label: 'Sessions' },
    { value: 'transactions', label: 'Transactions' },
    { value: 'cr', label: 'Conv. Rate' },
  ];

  const fmtMetric = (d) => {
    if (!d) return '-';
    if (metric === 'revenue') return d.revenue > 0 ? fmtCur(d.revenue) : '-';
    if (metric === 'sessions') return d.sessions > 0 ? fmtNum(d.sessions) : '-';
    if (metric === 'transactions') return d.transactions > 0 ? fmtNum(d.transactions) : '-';
    if (metric === 'cr') return d.sessions > 0 ? ((d.transactions / d.sessions) * 100).toFixed(2) + '%' : '-';
    return '-';
  };

  const rows = data?.rows || [];
  const channels = (data?.headers || []).filter(h => h !== 'Date' && h !== 'Total');
  const activeChannels = channels.filter(ch =>
    rows.some(row => (row[ch]?.revenue || 0) > 0 || (row[ch]?.sessions || 0) > 0)
  );
  // Show most recent dates first so "last 3 days" are at the top
  const displayRows = [...rows].reverse();

  const channelFooter = {};
  for (const ch of activeChannels) {
    channelFooter[ch] = { revenue: 0, sessions: 0, transactions: 0 };
    for (const row of rows) {
      const d = row[ch] || {};
      channelFooter[ch].revenue += d.revenue || 0;
      channelFooter[ch].sessions += d.sessions || 0;
      channelFooter[ch].transactions += d.transactions || 0;
    }
  }
  const grandTotal = {
    revenue: data?.totals?.['Total']?.revenue || 0,
    sessions: data?.totals?.['Total']?.sessions || 0,
    transactions: data?.totals?.['Total']?.transactions || 0,
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={toggleOpen}
        className="w-full p-6 border-b flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={18} className="text-gray-500" /> : <ChevronRight size={18} className="text-gray-500" />}
          <h3 className="font-semibold text-lg text-gray-800 text-left">
            Daily Channel Performance — Without Cost
            <span className="text-xs font-normal text-emerald-500 ml-1">(BigQuery)</span>
          </h3>
        </div>
        <span className="text-xs text-gray-500">{open ? 'Click to collapse' : 'Click to expand'}</span>
      </button>

      {open && (
        <>
          <div className="px-6 py-3 border-b flex items-center justify-end">
            <select
              value={metric}
              onChange={e => setMetric(e.target.value)}
              className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {metricOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : !rows.length ? (
            <div className="py-12 text-center text-gray-500 text-sm">No data available for the selected range.</div>
          ) : (
            <div className="overflow-x-auto" style={{ maxHeight: 600 }}>
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white z-10">
                  <tr className="bg-gray-50 text-left">
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap sticky left-0 bg-gray-50 z-20">Date</th>
                    {activeChannels.map(ch => (
                      <th key={ch} className="px-4 py-3 text-xs font-medium text-gray-500 text-right whitespace-nowrap">
                        <ChannelLabel name={ch} />
                      </th>
                    ))}
                    <th className="px-4 py-3 text-xs font-medium text-gray-500 text-right whitespace-nowrap">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {displayRows.map((row) => {
                    const totalData = row['Total'] || {};
                    return (
                      <tr key={row.date} className="bg-white hover:bg-gray-50">
                        <td className="px-4 py-2 font-medium text-gray-800 whitespace-nowrap sticky left-0 bg-white z-10">{formatDate(row.date)}</td>
                        {activeChannels.map(ch => {
                          const d = row[ch] || { revenue: 0, sessions: 0, transactions: 0 };
                          return (
                            <td key={ch} className="px-4 py-2 text-right">
                              <div className="text-gray-800">{fmtMetric(d)}</div>
                            </td>
                          );
                        })}
                        <td className="px-4 py-2 text-right">
                          <div className="font-medium text-green-700">{fmtMetric(totalData)}</div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="sticky bottom-0 bg-white z-10">
                  <tr className="border-t-2 border-gray-200">
                    <td className="px-4 py-3 font-semibold text-gray-900 sticky left-0 bg-white z-20">TOTAL</td>
                    {activeChannels.map(ch => (
                      <td key={ch} className="px-4 py-3 text-right">
                        <div className="font-semibold text-gray-800">{fmtMetric(channelFooter[ch])}</div>
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right">
                      <div className="font-semibold text-green-700">{fmtMetric(grandTotal)}</div>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Weekly Sales & Revenue Table ── */
function WeeklySalesTable({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiFetch('weekly-performance', startDate, endDate)
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading) return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
      </div>
    </div>
  );

  if (!data?.rows?.length) return null;

  const { rows, totals } = data;

  const formatWeek = (row) => {
    const ws = new Date(row.weekStart);
    const we = new Date(row.weekEnd);
    const fmtDay = (d) => `${d.getDate()} ${d.toLocaleString('tr-TR', { month: 'short' })}`;
    return `W${row.isoWeek}: ${fmtDay(ws)} - ${fmtDay(we)} ${we.getFullYear()}`;
  };

  const cols = [
    { key: 'week',          label: 'Hafta',          align: 'left',  fmt: (v, row) => formatWeek(row) },
    { key: 'sessions',      label: 'Sessions',       align: 'right', fmt: v => fmtNum(v) },
    { key: 'users',         label: 'Users',          align: 'right', fmt: v => fmtNum(v) },
    { key: 'newUsers',      label: 'New Users',      align: 'right', fmt: v => fmtNum(v) },
    { key: 'pageviews',     label: 'Pageviews',      align: 'right', fmt: v => fmtNum(v) },
    { key: 'addToCart',     label: 'Add to Cart',    align: 'right', fmt: v => fmtNum(v) },
    { key: 'beginCheckout', label: 'Checkout',       align: 'right', fmt: v => fmtNum(v) },
    { key: 'transactions',  label: 'Transactions',   align: 'right', fmt: v => fmtNum(v) },
    { key: 'revenue',       label: 'Revenue',        align: 'right', fmt: v => fmtCur(v) },
    { key: 'aov',           label: 'AOV',            align: 'right', fmt: v => fmtCur(v) },
    { key: 'convRate',      label: 'Conv. Rate',     align: 'right', fmt: v => v + '%' },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-6 border-b flex items-center justify-between">
        <h3 className="font-semibold text-lg text-gray-800">Haftalık Satış & Performans <span className="text-xs font-normal text-blue-500 ml-1">(BigQuery)</span></h3>
        <button onClick={() => downloadCSV(rows.map(r => ({ week: formatWeek(r), ...r })), 'bq-weekly-sales.csv')}
          className="p-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors">
          <Download className="w-4 h-4" />
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left">
              {cols.map(col => (
                <th key={col.key}
                  className={`px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50 transition-colors">
                {cols.map(col => (
                  <td key={col.key}
                    className={`px-4 py-2 text-sm ${col.align === 'right' ? 'text-right' : 'text-left'} ${
                      col.key === 'revenue' ? 'font-semibold text-green-700' :
                      col.key === 'transactions' ? 'font-semibold text-gray-900' :
                      col.key === 'week' ? 'font-medium text-gray-800 whitespace-nowrap' : 'text-gray-700'
                    }`}>
                    {col.fmt(row[col.key], row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot className="sticky bottom-0 bg-white z-10">
            <tr className="border-t-2 border-gray-200">
              {cols.map(col => (
                <td key={col.key}
                  className={`px-4 py-3 text-sm font-semibold ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                  {col.key === 'week' ? 'TOTAL' : col.fmt(totals[col.key], {})}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ── Monthly Sales & Revenue Table ── */
function MonthlySalesTable({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [spendByMonth, setSpendByMonth] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const brandId = localStorage.getItem('currentBrandId');
    Promise.all([
      apiFetch('monthly-performance', startDate, endDate),
      fetch(`https://api.glowytics.com/api/google-ads/unified-campaign-performance-monthly?dateRange=custom&startDate=${startDate}&endDate=${endDate}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' }
      }).then(r => r.ok ? r.json() : []).catch(() => [])
    ]).then(([bqData, spendRows]) => {
      setData(bqData);
      // Aggregate total cost per month (fix UTC timezone: +12h to get correct month)
      const toMonthKey = (v) => {
        if (!v) return '';
        const d = new Date(v);
        if (isNaN(d)) return '';
        d.setUTCHours(d.getUTCHours() + 12);
        return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      };
      const sm = {};
      for (const row of spendRows) {
        const mk = toMonthKey(row.month);
        if (!mk) continue;
        const cost = Number(row.google_cost || 0) + Number(row.meta_cost || 0) + Number(row.criteo_cost || 0) + Number(row.rtb_cost || 0) + Number(row.pinterest_cost || 0);
        sm[mk] = (sm[mk] || 0) + cost;
      }
      setSpendByMonth(sm);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading) return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
      </div>
    </div>
  );

  if (!data?.rows?.length) return null;

  const { rows, totals } = data;

  const formatMonth = (m) => {
    const [y, mo] = m.split('-');
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${names[parseInt(mo) - 1]} ${y}`;
  };

  // Enrich rows with cost/roas
  const enrichedRows = rows.map(r => {
    const cost = spendByMonth[r.month] || 0;
    const rev = Number(r.revenue || 0);
    return { ...r, cost, roas: cost > 0 ? (rev / cost).toFixed(2) + 'x' : '-' };
  });
  const totalCost = Object.values(spendByMonth).reduce((s, v) => s + v, 0);
  const totalRoas = totalCost > 0 ? (Number(totals.revenue || 0) / totalCost).toFixed(2) + 'x' : '-';

  const cols = [
    { key: 'month',        label: 'Month',          align: 'left',  fmt: v => formatMonth(v) },
    { key: 'sessions',     label: 'Sessions',       align: 'right', fmt: v => fmtNum(v) },
    { key: 'users',        label: 'Users',          align: 'right', fmt: v => fmtNum(v) },
    { key: 'newUsers',     label: 'New Users',      align: 'right', fmt: v => fmtNum(v) },
    { key: 'pageviews',    label: 'Pageviews',      align: 'right', fmt: v => fmtNum(v) },
    { key: 'addToCart',    label: 'Add to Cart',    align: 'right', fmt: v => fmtNum(v) },
    { key: 'beginCheckout',label: 'Checkout',       align: 'right', fmt: v => fmtNum(v) },
    { key: 'transactions', label: 'Transactions',   align: 'right', fmt: v => fmtNum(v) },
    { key: 'revenue',      label: 'Revenue',        align: 'right', fmt: v => fmtCur(v) },
    { key: 'cost',         label: 'Cost',           align: 'right', fmt: v => fmtCur(v) },
    { key: 'roas',         label: 'ROAS',           align: 'right', fmt: v => v },
    { key: 'aov',          label: 'AOV',            align: 'right', fmt: v => fmtCur(v) },
    { key: 'convRate',     label: 'Conv. Rate',     align: 'right', fmt: v => v + '%' },
  ];

  const enrichedTotals = { ...totals, cost: totalCost, roas: totalRoas };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-6 border-b flex items-center justify-between">
        <h3 className="font-semibold text-lg text-gray-800">Monthly Sales & Revenue <span className="text-xs font-normal text-blue-500 ml-1">(BigQuery)</span></h3>
        <button onClick={() => downloadCSV(enrichedRows, 'bq-monthly-sales.csv')}
          className="p-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors">
          <Download className="w-4 h-4" />
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left">
              {cols.map((col) => (
                <th key={col.key}
                  className={`px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {enrichedRows.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50 transition-colors">
                {cols.map((col) => (
                  <td key={col.key}
                    className={`px-4 py-2 text-sm ${col.align === 'right' ? 'text-right' : 'text-left'} ${col.key === 'revenue' ? 'font-semibold text-green-700' : col.key === 'cost' ? 'font-semibold text-red-600' : col.key === 'roas' ? 'font-semibold text-blue-600' : col.key === 'transactions' ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                    {col.fmt(row[col.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot className="sticky bottom-0 bg-white z-10">
            <tr className="border-t-2 border-gray-200">
              {cols.map((col) => (
                <td key={col.key}
                  className={`px-4 py-3 text-sm font-semibold ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                  {col.key === 'month' ? 'TOTAL' : col.fmt(enrichedTotals[col.key])}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ── Daily Sales & Revenue (BigQuery) — collapsible, daily equivalent of MonthlySalesTable ── */
function DailySalesRevenueTable({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [spendByDate, setSpendByDate] = useState({});
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [fetched, setFetched] = useState(false);

  useEffect(() => {
    setFetched(false);
    if (open) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const fetchData = () => {
    setLoading(true);
    const brandId = localStorage.getItem('currentBrandId');
    Promise.all([
      apiFetch('daily-performance', startDate, endDate),
      fetch(`https://api.glowytics.com/api/google-ads/unified-campaign-performance?dateRange=custom&startDate=${startDate}&endDate=${endDate}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId || '' }
      }).then(r => r.ok ? r.json() : []).catch(() => [])
    ]).then(([bqData, spendRows]) => {
      setData(bqData);
      const toDateKey = (v) => {
        if (!v) return '';
        const d = new Date(v);
        if (isNaN(d)) return '';
        return d.toISOString().slice(0, 10);
      };
      const sm = {};
      if (Array.isArray(spendRows)) {
        for (const row of spendRows) {
          const dk = toDateKey(row.date || row.day);
          if (!dk) continue;
          const cost = Number(row.google_cost || 0) + Number(row.meta_cost || 0) + Number(row.criteo_cost || 0) + Number(row.rtb_cost || 0) + Number(row.pinterest_cost || 0);
          sm[dk] = (sm[dk] || 0) + cost;
        }
      }
      setSpendByDate(sm);
      setFetched(true);
    }).catch(console.error)
      .finally(() => setLoading(false));
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && !fetched) fetchData();
  };

  const formatDate = (d) => {
    const dt = new Date(d);
    if (isNaN(dt)) return d;
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dowNames = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
    const dow = (dt.getUTCDay() + 6) % 7;
    return `${dt.getUTCDate()} ${names[dt.getUTCMonth()]} ${dt.getUTCFullYear()} · ${dowNames[dow]}`;
  };

  const rows = data?.rows || [];
  const totals = data?.totals || {};

  // Enrich with cost/roas, most recent first
  const enrichedRows = [...rows].map(r => {
    const cost = spendByDate[r.date] || 0;
    const rev = Number(r.revenue || 0);
    return { ...r, cost, roas: cost > 0 ? (rev / cost).toFixed(2) + 'x' : '-' };
  }).sort((a, b) => new Date(b.date) - new Date(a.date));

  const totalCost = Object.values(spendByDate).reduce((s, v) => s + v, 0);
  const totalRoas = totalCost > 0 ? (Number(totals.revenue || 0) / totalCost).toFixed(2) + 'x' : '-';
  const enrichedTotals = { ...totals, cost: totalCost, roas: totalRoas };

  const cols = [
    { key: 'date',         label: 'Date',           align: 'left',  fmt: v => formatDate(v) },
    { key: 'sessions',     label: 'Sessions',       align: 'right', fmt: v => fmtNum(v) },
    { key: 'users',        label: 'Users',          align: 'right', fmt: v => fmtNum(v) },
    { key: 'newUsers',     label: 'New Users',      align: 'right', fmt: v => fmtNum(v) },
    { key: 'pageviews',    label: 'Pageviews',      align: 'right', fmt: v => fmtNum(v) },
    { key: 'addToCart',    label: 'Add to Cart',    align: 'right', fmt: v => fmtNum(v) },
    { key: 'beginCheckout',label: 'Checkout',       align: 'right', fmt: v => fmtNum(v) },
    { key: 'transactions', label: 'Transactions',   align: 'right', fmt: v => fmtNum(v) },
    { key: 'revenue',      label: 'Revenue',        align: 'right', fmt: v => fmtCur(v) },
    { key: 'cost',         label: 'Cost',           align: 'right', fmt: v => fmtCur(v) },
    { key: 'roas',         label: 'ROAS',           align: 'right', fmt: v => v },
    { key: 'aov',          label: 'AOV',            align: 'right', fmt: v => fmtCur(v) },
    { key: 'convRate',     label: 'Conv. Rate',     align: 'right', fmt: v => v + '%' },
  ];

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={toggleOpen}
        className="w-full p-6 border-b flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={18} className="text-gray-500" /> : <ChevronRight size={18} className="text-gray-500" />}
          <h3 className="font-semibold text-lg text-gray-800 text-left">
            Daily Sales & Revenue
            <span className="text-xs font-normal text-blue-500 ml-1">(BigQuery)</span>
          </h3>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">{open ? 'Click to collapse' : 'Click to expand'}</span>
          {open && enrichedRows.length > 0 && (
            <span
              onClick={e => { e.stopPropagation(); downloadCSV(enrichedRows, 'bq-daily-sales.csv'); }}
              className="p-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors cursor-pointer"
              role="button"
              title="Download CSV"
            >
              <Download className="w-4 h-4" />
            </span>
          )}
        </div>
      </button>

      {open && (
        <>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : !enrichedRows.length ? (
            <div className="py-12 text-center text-gray-500 text-sm">No data available for the selected range.</div>
          ) : (
            <div className="overflow-x-auto" style={{ maxHeight: 600 }}>
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr className="text-left">
                    {cols.map((col) => (
                      <th key={col.key}
                        className={`px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {enrichedRows.map((row, i) => (
                    <tr key={i} className="hover:bg-gray-50 transition-colors">
                      {cols.map((col) => (
                        <td key={col.key}
                          className={`px-4 py-2 text-sm whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'} ${col.key === 'revenue' ? 'font-semibold text-green-700' : col.key === 'cost' ? 'font-semibold text-red-600' : col.key === 'roas' ? 'font-semibold text-blue-600' : col.key === 'transactions' ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                          {col.fmt(row[col.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
                <tfoot className="sticky bottom-0 bg-white z-10">
                  <tr className="border-t-2 border-gray-200">
                    {cols.map((col) => (
                      <td key={col.key}
                        className={`px-4 py-3 text-sm font-semibold ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                        {col.key === 'date' ? 'TOTAL' : col.fmt(enrichedTotals[col.key])}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ── Month-over-Month Daily Line Chart ── */
function MonthOverMonthChart({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [metric, setMetric] = useState('revenue');

  useEffect(() => {
    setFetched(false);
    if (open) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const fetchData = () => {
    setLoading(true);
    apiFetch('daily-performance', startDate, endDate)
      .then(d => { setData(d); setFetched(true); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next && !fetched) fetchData();
  };

  const METRICS = [
    { key: 'revenue',       label: 'Revenue',      fmt: v => fmtCur(v) },
    { key: 'transactions',  label: 'Transactions', fmt: v => fmtNum(v) },
    { key: 'sessions',      label: 'Sessions',     fmt: v => fmtNum(v) },
    { key: 'users',         label: 'Users',        fmt: v => fmtNum(v) },
    { key: 'newUsers',      label: 'New Users',    fmt: v => fmtNum(v) },
    { key: 'pageviews',     label: 'Pageviews',    fmt: v => fmtNum(v) },
    { key: 'addToCart',     label: 'Add to Cart',  fmt: v => fmtNum(v) },
    { key: 'beginCheckout', label: 'Checkout',     fmt: v => fmtNum(v) },
  ];

  const currentMetric = METRICS.find(m => m.key === metric) || METRICS[0];

  // Pivot daily rows by month: [{day: 1, "2026-01": 1234, "2026-02": 5678, ...}, ...]
  const { chartData, monthKeys, monthLabels, maxDay } = useMemo(() => {
    const rows = data?.rows || [];
    const monthSet = new Set();
    const dayMap = {}; // day -> { day, monthKey: value }
    let maxDay = 0;

    for (const r of rows) {
      if (!r.date) continue;
      const d = new Date(r.date);
      if (isNaN(d)) continue;
      const monthKey = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
      const day = d.getUTCDate();
      if (day > maxDay) maxDay = day;
      monthSet.add(monthKey);
      if (!dayMap[day]) dayMap[day] = { day };
      dayMap[day][monthKey] = Number(r[metric] || 0);
    }

    const lastDay = Math.max(maxDay, 28);
    const days = Array.from({ length: lastDay }, (_, i) => i + 1);
    const chartData = days.map(d => dayMap[d] || { day: d });
    const monthKeys = Array.from(monthSet).sort();
    const monthNames = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
    const monthLabels = {};
    for (const k of monthKeys) {
      const [y, m] = k.split('-');
      monthLabels[k] = `${monthNames[parseInt(m, 10) - 1]} ${y}`;
    }
    return { chartData, monthKeys, monthLabels, maxDay: lastDay };
  }, [data, metric]);

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <button
        type="button"
        onClick={toggleOpen}
        className="w-full p-6 border-b flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={18} className="text-gray-500" /> : <ChevronRight size={18} className="text-gray-500" />}
          <h3 className="font-semibold text-lg text-gray-800 text-left">
            Month-over-Month Daily Comparison
            <span className="text-xs font-normal text-blue-500 ml-1">(BigQuery)</span>
          </h3>
        </div>
        <span className="text-xs text-gray-500">{open ? 'Click to collapse' : 'Click to expand'}</span>
      </button>

      {open && (
        <div className="p-6">
          <div className="mb-4 flex items-center gap-3 flex-wrap">
            <span className="text-sm font-medium text-gray-700">Metric:</span>
            <select
              value={metric}
              onChange={e => setMetric(e.target.value)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {METRICS.map(m => (
                <option key={m.key} value={m.key}>{m.label}</option>
              ))}
            </select>
            {monthKeys.length > 0 && (
              <span className="text-xs text-gray-500 ml-2">{monthKeys.length} ay · {maxDay} gün</span>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
            </div>
          ) : !monthKeys.length ? (
            <div className="py-12 text-center text-gray-500 text-sm">No data available for the selected range.</div>
          ) : (
            <ResponsiveContainer width="100%" height={440}>
              <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="day"
                  tick={{ fill: '#9ca3af', fontSize: 11 }}
                  axisLine={{ stroke: '#e5e7eb' }}
                  tickLine={false}
                  label={{ value: 'Ayın günü', position: 'insideBottom', offset: -8, fill: '#6b7280', fontSize: 12 }}
                />
                <YAxis
                  tick={{ fill: '#9ca3af', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={v => v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  labelFormatter={d => `Gün ${d}`}
                  formatter={(v, name) => [currentMetric.fmt(v), monthLabels[name] || name]}
                />
                <Legend
                  iconType="circle"
                  wrapperStyle={{ paddingTop: 12 }}
                  formatter={value => monthLabels[value] || value}
                />
                {monthKeys.map((mk, i) => (
                  <Line
                    key={mk}
                    type="natural"
                    dataKey={mk}
                    name={mk}
                    stroke={COLORS[i % COLORS.length]}
                    strokeWidth={3}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    dot={false}
                    activeDot={{ r: 7, strokeWidth: 3, stroke: COLORS[i % COLORS.length], fill: '#fff' }}
                    connectNulls
                    animationDuration={800}
                    animationEasing="ease-out"
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      )}
    </div>
  );
}

/* ── Daily Sales Table ── */
/* ── Week × Day metric matrix ── */
const MATRIX_METRICS = [
  { key: 'revenue',       label: 'Revenue',       fmt: v => fmtCur(v),           color: '#10b981' },
  { key: 'sessions',      label: 'Sessions',      fmt: v => fmtNum(Math.round(v)), color: '#06b6d4' },
  { key: 'users',         label: 'Users',         fmt: v => fmtNum(Math.round(v)), color: '#6366f1' },
  { key: 'newUsers',      label: 'New Users',     fmt: v => fmtNum(Math.round(v)), color: '#8b5cf6' },
  { key: 'pageviews',     label: 'Pageviews',     fmt: v => fmtNum(Math.round(v)), color: '#0ea5e9' },
  { key: 'transactions',  label: 'Transactions',  fmt: v => fmtNum(Math.round(v)), color: '#f59e0b' },
  { key: 'addToCart',     label: 'Add to Cart',   fmt: v => fmtNum(Math.round(v)), color: '#ec4899' },
  { key: 'beginCheckout', label: 'Checkout',      fmt: v => fmtNum(Math.round(v)), color: '#f97316' },
  { key: 'aov',           label: 'AOV',           fmt: v => fmtCur(v),           color: '#059669', derived: true },
  { key: 'convRate',      label: 'Conv. Rate',    fmt: v => (v || 0).toFixed(2) + '%', color: '#db2777', derived: true },
];

const DOW_LABELS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz']; // Monday-first

// Format a Date to YYYY-MM-DD using LOCAL components (toISOString would shift to UTC
// and roll back a day in UTC+ timezones → off-by-one week labels)
function toLocalYMD(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Convert Date → Monday (start of ISO week) as YYYY-MM-DD
function getMondayStr(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.getDay(); // 0 = Sun, 1 = Mon, ..., 6 = Sat
  const diff = day === 0 ? -6 : 1 - day; // days to Monday
  d.setDate(d.getDate() + diff);
  return toLocalYMD(d);
}

// Monday of week index (0 = Mon, 6 = Sun) for a YYYY-MM-DD string
function weekdayIndex(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const day = d.getDay(); // 0 = Sun
  return day === 0 ? 6 : day - 1;
}

function formatWeekLabel(mondayStr) {
  const mon = new Date(mondayStr + 'T00:00:00');
  const sun = new Date(mon); sun.setDate(sun.getDate() + 6);
  const dMon = mon.getDate();
  const dSun = sun.getDate();
  const monthMon = mon.toLocaleString('tr-TR', { month: 'short' });
  const monthSun = sun.toLocaleString('tr-TR', { month: 'short' });
  const yearSun = sun.getFullYear();
  if (monthMon === monthSun) {
    return `${dMon}-${dSun} ${monthMon} ${yearSun}`;
  }
  return `${dMon} ${monthMon} - ${dSun} ${monthSun} ${yearSun}`;
}

function WeekDayMatrixTable({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState('revenue');

  useEffect(() => {
    setLoading(true);
    apiFetch('summary', startDate, endDate)
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  const metricDef = MATRIX_METRICS.find(m => m.key === metric) || MATRIX_METRICS[0];

  const { weeks, weekTotals, dayTotals, grandTotal, maxCell } = useMemo(() => {
    if (!data?.timeseries?.length) {
      return { weeks: [], weekTotals: {}, dayTotals: Array(7).fill(0), grandTotal: 0, maxCell: 0 };
    }

    const getMetricValue = (row) => {
      if (metricDef.key === 'aov') {
        return row.transactions > 0 ? row.revenue / row.transactions : 0;
      }
      if (metricDef.key === 'convRate') {
        return row.sessions > 0 ? (row.transactions / row.sessions) * 100 : 0;
      }
      return Number(row[metricDef.key] || 0);
    };

    // Group rows by Monday
    const weekMap = new Map(); // mondayStr → Array(7) of { value, rawRow }
    for (const row of data.timeseries) {
      if (!row?.date) continue;
      const mondayStr = getMondayStr(row.date);
      const dow = weekdayIndex(row.date);
      if (!weekMap.has(mondayStr)) weekMap.set(mondayStr, Array(7).fill(null));
      weekMap.get(mondayStr)[dow] = { value: getMetricValue(row), row };
    }

    const sortedMondays = Array.from(weekMap.keys()).sort((a, b) => b.localeCompare(a));
    const wks = sortedMondays.map(m => ({ monday: m, cells: weekMap.get(m) }));

    // Compute totals — for derived metrics, sum underlying numerators/denominators and recompute
    const computeSumOrDerived = (rows) => {
      if (metricDef.key === 'aov') {
        const rev = rows.reduce((s, r) => s + (r?.row?.revenue || 0), 0);
        const txn = rows.reduce((s, r) => s + (r?.row?.transactions || 0), 0);
        return txn > 0 ? rev / txn : 0;
      }
      if (metricDef.key === 'convRate') {
        const txn = rows.reduce((s, r) => s + (r?.row?.transactions || 0), 0);
        const ses = rows.reduce((s, r) => s + (r?.row?.sessions || 0), 0);
        return ses > 0 ? (txn / ses) * 100 : 0;
      }
      return rows.reduce((s, r) => s + (r?.value || 0), 0);
    };

    const wkTotals = {};
    for (const w of wks) {
      const present = w.cells.filter(c => c != null);
      wkTotals[w.monday] = computeSumOrDerived(present);
    }

    const dayTots = Array(7).fill(0).map((_, dayIdx) => {
      const all = wks.map(w => w.cells[dayIdx]).filter(c => c != null);
      return computeSumOrDerived(all);
    });

    const allCellsFlat = wks.flatMap(w => w.cells).filter(c => c != null);
    const grand = computeSumOrDerived(allCellsFlat);

    const cellValues = allCellsFlat.map(c => c.value).filter(v => v > 0);
    const maxC = cellValues.length ? Math.max(...cellValues) : 0;

    return { weeks: wks, weekTotals: wkTotals, dayTotals: dayTots, grandTotal: grand, maxCell: maxC };
  }, [data, metric, metricDef]);

  if (loading) return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
      </div>
    </div>
  );

  if (!weeks.length) return null;

  // Heat color for cell — light to darker tint of metric color based on value/maxCell
  const cellBg = (v) => {
    if (!v || maxCell <= 0) return '';
    const ratio = Math.min(1, v / maxCell);
    if (ratio < 0.05) return '';
    // Alpha between 0.08 and 0.55
    const alpha = 0.08 + ratio * 0.47;
    // Convert metricDef.color hex to rgba
    const hex = metricDef.color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha.toFixed(2)})`;
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-6 border-b flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-semibold text-lg text-gray-800">
            Haftalık Gün Karşılaştırması
            <span className="text-xs font-normal text-blue-500 ml-1">(BigQuery)</span>
          </h3>
          <p className="text-xs text-gray-500 mt-1">Satırlar = haftalar (Pzt başlangıç) · Sütunlar = günler</p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-xs text-gray-500 font-medium">Metrik:</label>
          <select
            value={metric}
            onChange={e => setMetric(e.target.value)}
            className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            {MATRIX_METRICS.map(m => (
              <option key={m.key} value={m.key}>{m.label}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-xs font-medium text-gray-500 text-left whitespace-nowrap">Hafta</th>
              {DOW_LABELS.map(d => (
                <th key={d} className="px-4 py-3 text-xs font-medium text-gray-500 text-right whitespace-nowrap">{d}</th>
              ))}
              <th className="px-4 py-3 text-xs font-medium text-gray-500 text-right whitespace-nowrap border-l border-gray-200">Toplam</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {weeks.map(w => (
              <tr key={w.monday} className="hover:bg-gray-50/60 transition-colors">
                <td className="px-4 py-2 text-sm font-medium text-gray-800 whitespace-nowrap">
                  {formatWeekLabel(w.monday)}
                </td>
                {w.cells.map((cell, idx) => (
                  <td
                    key={idx}
                    className="px-4 py-2 text-sm text-right text-gray-700 whitespace-nowrap"
                    style={{ backgroundColor: cell ? cellBg(cell.value) : '' }}
                  >
                    {cell ? metricDef.fmt(cell.value) : <span className="text-gray-300">—</span>}
                  </td>
                ))}
                <td className="px-4 py-2 text-sm text-right font-semibold text-gray-900 whitespace-nowrap border-l border-gray-200">
                  {metricDef.fmt(weekTotals[w.monday] || 0)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-200 bg-gray-50/60">
              <td className="px-4 py-3 text-sm font-semibold text-gray-800">Gün Toplamı</td>
              {dayTotals.map((t, i) => (
                <td key={i} className="px-4 py-3 text-sm text-right font-semibold text-gray-800 whitespace-nowrap">
                  {metricDef.fmt(t)}
                </td>
              ))}
              <td className="px-4 py-3 text-sm text-right font-bold text-gray-900 whitespace-nowrap border-l border-gray-200">
                {metricDef.fmt(grandTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

function DailySalesTable({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiFetch('summary', startDate, endDate)
      .then(d => setData(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [startDate, endDate]);

  if (loading) return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
      </div>
    </div>
  );

  if (!data?.timeseries?.length) return null;

  const rows = [...data.timeseries]
    .filter(r => {
      const y = r.date?.slice(0, 4);
      return y && parseInt(y, 10) >= 2026;
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const totals = rows.reduce((acc, r) => {
    acc.users += r.users || 0;
    acc.sessions += r.sessions || 0;
    acc.ga4Sessions += r.ga4Sessions || 0;
    acc.newUsers += r.newUsers || 0;
    acc.pageviews += r.pageviews || 0;
    acc.addToCart += r.addToCart || 0;
    acc.beginCheckout += r.beginCheckout || 0;
    acc.transactions += r.transactions || 0;
    acc.revenue += r.revenue || 0;
    return acc;
  }, { users: 0, sessions: 0, ga4Sessions: 0, newUsers: 0, pageviews: 0, addToCart: 0, beginCheckout: 0, transactions: 0, revenue: 0 });
  totals.aov = totals.transactions > 0 ? totals.revenue / totals.transactions : 0;
  totals.convRate = totals.sessions > 0 ? ((totals.transactions / totals.sessions) * 100).toFixed(2) : '0.00';

  const formatDate = (d) => {
    const dt = new Date(d);
    return `${dt.getDate()} ${dt.toLocaleString('tr-TR', { month: 'short' })} ${dt.getFullYear()}`;
  };

  const cols = [
    { key: 'date',          label: 'Tarih',         align: 'left',  fmt: v => formatDate(v) },
    { key: 'sessions',      label: 'Sessions',      align: 'right', fmt: v => fmtNum(v), sum: true },
    { key: 'ga4Sessions',   label: 'GA4 Sessions',  align: 'right', fmt: v => v == null ? '—' : fmtNum(v), sum: true },
    { key: 'users',         label: 'Users',         align: 'right', fmt: v => fmtNum(v), sum: true },
    { key: 'newUsers',      label: 'New Users',     align: 'right', fmt: v => fmtNum(v), sum: true },
    { key: 'pageviews',     label: 'Pageviews',     align: 'right', fmt: v => fmtNum(v), sum: true },
    { key: 'addToCart',     label: 'Add to Cart',   align: 'right', fmt: v => fmtNum(v), sum: true },
    { key: 'beginCheckout', label: 'Checkout',      align: 'right', fmt: v => fmtNum(v), sum: true },
    { key: 'transactions',  label: 'Transactions',  align: 'right', fmt: v => fmtNum(v), sum: true },
    { key: 'revenue',       label: 'Revenue',       align: 'right', fmt: v => fmtCur(v), sum: true },
    { key: 'aov',           label: 'AOV',           align: 'right', fmt: v => fmtCur(v) },
    { key: 'convRate',      label: 'Conv. Rate',    align: 'right', fmt: v => v + '%' },
  ];

  const enriched = rows.map(r => ({
    ...r,
    aov: r.transactions > 0 ? r.revenue / r.transactions : 0,
    convRate: r.sessions > 0 ? ((r.transactions / r.sessions) * 100).toFixed(2) : '0.00',
  }));

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-6 border-b flex items-center justify-between">
        <h3 className="font-semibold text-lg text-gray-800">Günlük Satış & Performans <span className="text-xs font-normal text-blue-500 ml-1">(BigQuery)</span></h3>
        <button onClick={() => downloadCSV(enriched, 'bq-daily-sales.csv')}
          className="p-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors">
          <Download className="w-4 h-4" />
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left">
              {cols.map(col => (
                <th key={col.key}
                  className={`px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {enriched.map((row, i) => (
              <tr key={i} className="hover:bg-gray-50 transition-colors">
                {cols.map(col => (
                  <td key={col.key}
                    className={`px-4 py-2 text-sm ${col.align === 'right' ? 'text-right' : 'text-left'} ${
                      col.key === 'revenue' ? 'font-semibold text-green-700' :
                      col.key === 'transactions' ? 'font-semibold text-gray-900' :
                      col.key === 'date' ? 'font-medium text-gray-800 whitespace-nowrap' : 'text-gray-700'
                    }`}>
                    {col.fmt(row[col.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          <tfoot className="sticky bottom-0 bg-white z-10">
            <tr className="border-t-2 border-gray-200">
              {cols.map(col => (
                <td key={col.key}
                  className={`px-4 py-3 text-sm font-semibold ${col.align === 'right' ? 'text-right' : 'text-left'}`}>
                  {col.key === 'date' ? 'TOPLAM' : col.fmt(totals[col.key])}
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ── Campaign Performance: BQ revenue next to platform cost ──
 * Weekly totals on top (revenue and cost side by side), campaign detail below.
 * Revenue is the GA4 BigQuery export; cost is each ad platform's own campaign
 * table, joined on the campaign name. */
function CampaignPerformanceTable({ startDate, endDate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [granularity, setGranularity] = useState('week');
  const [search, setSearch] = useState('');
  const [channel, setChannel] = useState('all');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    setLoading(true);
    const brandId = localStorage.getItem('currentBrandId');
    fetch(`${API}/campaign-performance?startDate=${startDate}&endDate=${endDate}&granularity=${granularity}`, {
      credentials: 'include',
      headers: { 'X-Brand-ID': brandId || '' }
    })
      .then(r => r.ok ? r.json() : { ok: false, rows: [], periods: [] })
      .then(setData)
      .catch(() => setData({ ok: false, rows: [], periods: [] }))
      .finally(() => setLoading(false));
  }, [startDate, endDate, granularity]);

  const periodLabel = useCallback((p) => {
    if (granularity === 'month') return p;
    const s = new Date(p + 'T00:00:00');
    if (isNaN(s)) return p;
    const e = new Date(s); e.setDate(e.getDate() + 6);
    const f = (d) => d.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
    return `${f(s)} – ${f(e)}`;
  }, [granularity]);

  const money = (n) => '₺' + Math.round(Number(n || 0)).toLocaleString('tr-TR');

  if (loading) return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
      </div>
    </div>
  );

  if (!data?.rows?.length) return null;

  const { periods, rows, periodTotals, totals } = data;

  const channels = [...new Set(rows.map(r => r.channel))].sort();
  const filtered = rows.filter(r =>
    (channel === 'all' || r.channel === channel) &&
    (!search || String(r.campaign || '').toLowerCase().includes(search.toLowerCase()))
  );
  const visible = showAll ? filtered : filtered.slice(0, 25);

  // The footer sums what is on screen, so filtering the table changes it too.
  const footer = periods.reduce((acc, p) => {
    acc.byPeriod[p] = { revenue: 0, cost: 0 };
    return acc;
  }, { byPeriod: {}, revenue: 0, cost: 0 });
  for (const r of filtered) {
    for (const p of periods) {
      const v = r.byPeriod[p];
      if (!v) continue;
      footer.byPeriod[p].revenue += v.revenue;
      footer.byPeriod[p].cost += v.cost;
    }
    footer.revenue += r.total.revenue;
    footer.cost += r.total.cost;
  }

  const chartData = periods.map(p => ({
    period: periodLabel(p),
    revenue: Math.round(periodTotals[p]?.revenue || 0),
    cost: Math.round(periodTotals[p]?.cost || 0),
    roas: periodTotals[p]?.roas ?? null,
  }));

  const csvRows = filtered.map(r => {
    const out = { campaign: r.campaign, channel: r.channel };
    for (const p of periods) {
      out[`${p} revenue`] = r.byPeriod[p]?.revenue ?? 0;
      out[`${p} cost`] = r.byPeriod[p]?.cost ?? 0;
    }
    out['total revenue'] = r.total.revenue;
    out['total cost'] = r.total.cost;
    out['total roas'] = r.total.roas ?? '';
    return out;
  });

  const roasColor = (roas) => roas == null ? 'text-gray-400' : roas >= 3 ? 'text-emerald-600' : roas >= 1 ? 'text-amber-600' : 'text-red-500';

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="p-6 border-b flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold text-lg text-gray-800">
            Kampanya Performansı <span className="text-xs font-normal text-blue-500 ml-1">(BigQuery gelir + mecra maliyeti)</span>
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            Gelir GA4 BigQuery export'undan, maliyet Google / Meta / Criteo / TikTok / Pinterest / RTB House kampanya tablolarından. Eşleşme kampanya adı üzerinden.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-gray-50 p-0.5 rounded-lg">
            {[{ k: 'week', l: 'Haftalık' }, { k: 'month', l: 'Aylık' }].map(g => (
              <button key={g.k} onClick={() => setGranularity(g.k)}
                className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                  granularity === g.k ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-white hover:text-gray-800'
                }`}>{g.l}</button>
            ))}
          </div>
          <button onClick={() => downloadCSV(csvRows, `bq-campaign-performance-${granularity}.csv`)}
            className="p-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors">
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Top: period totals — revenue and cost side by side */}
      <div className="p-6 border-b bg-gray-50/60">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-white rounded-xl border border-gray-100 p-3">
            <p className="text-xs text-gray-500">Toplam Gelir (BQ)</p>
            <p className="text-lg font-semibold text-green-700 mt-0.5">{money(totals.revenue)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-3">
            <p className="text-xs text-gray-500">Toplam Maliyet</p>
            <p className="text-lg font-semibold text-red-600 mt-0.5">{money(totals.cost)}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-3">
            <p className="text-xs text-gray-500">ROAS</p>
            <p className={`text-lg font-semibold mt-0.5 ${roasColor(totals.roas)}`}>{totals.roas != null ? totals.roas.toFixed(2) + 'x' : '-'}</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 p-3">
            <p className="text-xs text-gray-500">Kampanya</p>
            <p className="text-lg font-semibold text-gray-900 mt-0.5">{fmtNum(rows.length)}</p>
          </div>
        </div>

        <div style={{ width: '100%', height: 260 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="period" tick={{ fontSize: 11, fill: '#6b7280' }} interval={0} angle={chartData.length > 8 ? -25 : 0} textAnchor={chartData.length > 8 ? 'end' : 'middle'} height={chartData.length > 8 ? 60 : 30} />
              <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={v => (v / 1000).toLocaleString('tr-TR') + 'K'} />
              <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => [money(v), n === 'revenue' ? 'Gelir (BQ)' : 'Maliyet']} />
              <Legend formatter={v => v === 'revenue' ? 'Gelir (BQ)' : 'Maliyet'} />
              <Bar dataKey="revenue" fill="#16a34a" radius={[4, 4, 0, 0]} />
              <Bar dataKey="cost" fill="#ef4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="overflow-x-auto mt-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-white text-left">
                <th className="px-3 py-2 text-xs font-medium text-gray-500 whitespace-nowrap sticky left-0 bg-white">{granularity === 'week' ? 'Hafta' : 'Ay'}</th>
                {periods.map(p => (
                  <th key={p} className="px-3 py-2 text-xs font-medium text-gray-500 text-right whitespace-nowrap">{periodLabel(p)}</th>
                ))}
                <th className="px-3 py-2 text-xs font-medium text-gray-500 text-right whitespace-nowrap">Toplam</th>
              </tr>
            </thead>
            <tbody className="divide-y bg-white">
              <tr>
                <td className="px-3 py-2 font-medium text-gray-700 whitespace-nowrap sticky left-0 bg-white">Gelir (BQ)</td>
                {periods.map(p => <td key={p} className="px-3 py-2 text-right text-green-700 font-medium whitespace-nowrap">{money(periodTotals[p]?.revenue)}</td>)}
                <td className="px-3 py-2 text-right text-green-700 font-semibold whitespace-nowrap">{money(totals.revenue)}</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-medium text-gray-700 whitespace-nowrap sticky left-0 bg-white">Maliyet</td>
                {periods.map(p => <td key={p} className="px-3 py-2 text-right text-red-600 whitespace-nowrap">{money(periodTotals[p]?.cost)}</td>)}
                <td className="px-3 py-2 text-right text-red-600 font-semibold whitespace-nowrap">{money(totals.cost)}</td>
              </tr>
              <tr>
                <td className="px-3 py-2 font-medium text-gray-700 whitespace-nowrap sticky left-0 bg-white">ROAS</td>
                {periods.map(p => {
                  const r = periodTotals[p]?.roas;
                  return <td key={p} className={`px-3 py-2 text-right whitespace-nowrap ${roasColor(r)}`}>{r != null ? r.toFixed(2) + 'x' : '-'}</td>;
                })}
                <td className={`px-3 py-2 text-right font-semibold whitespace-nowrap ${roasColor(totals.roas)}`}>{totals.roas != null ? totals.roas.toFixed(2) + 'x' : '-'}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Campaign detail */}
      <div className="px-6 py-3 border-b flex flex-wrap items-center gap-2">
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); setShowAll(false); }}
          placeholder="Kampanya ara..."
          className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <select value={channel} onChange={e => { setChannel(e.target.value); setShowAll(false); }}
          className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="all">Tüm mecralar</option>
          {channels.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="text-xs text-gray-500">{fmtNum(filtered.length)} kampanya</span>
      </div>

      <div className="overflow-x-auto" style={{ maxHeight: 640 }}>
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white z-10">
            <tr className="bg-gray-50 text-left">
              <th className="px-4 py-3 text-xs font-medium text-gray-500 whitespace-nowrap sticky left-0 bg-gray-50 z-20">Kampanya</th>
              {periods.map(p => (
                <th key={p} className="px-4 py-3 text-xs font-medium text-gray-500 text-right whitespace-nowrap">{periodLabel(p)}</th>
              ))}
              <th className="px-4 py-3 text-xs font-medium text-gray-500 text-right whitespace-nowrap">Toplam</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {visible.map((r, i) => (
              <tr key={r.campaign + i} className="bg-white hover:bg-gray-50">
                <td className="px-4 py-2 sticky left-0 bg-white z-10 max-w-[280px]">
                  <div className="font-medium text-gray-800 truncate" title={r.campaign}>{r.campaign}</div>
                  <div className="text-[10px] text-gray-400">{r.channel}{!r.matched && r.total.cost > 0 ? ' · gelir eşleşmedi' : ''}</div>
                </td>
                {periods.map(p => {
                  const v = r.byPeriod[p];
                  if (!v || (v.revenue === 0 && v.cost === 0)) return <td key={p} className="px-4 py-2 text-right text-gray-300">-</td>;
                  return (
                    <td key={p} className="px-4 py-2 text-right whitespace-nowrap">
                      <div className="text-green-700">{v.revenue > 0 ? money(v.revenue) : '-'}</div>
                      {v.cost > 0 && <div className="text-[10px] text-red-400 leading-tight">maliyet: {money(v.cost)}</div>}
                      {v.roas != null && <div className={`text-[10px] leading-tight ${roasColor(v.roas)}`}>{v.roas.toFixed(2)}x</div>}
                    </td>
                  );
                })}
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <div className="font-semibold text-green-700">{money(r.total.revenue)}</div>
                  {r.total.cost > 0 && <div className="text-[10px] text-red-400 leading-tight">maliyet: {money(r.total.cost)}</div>}
                  {r.total.roas != null && <div className={`text-[10px] font-medium leading-tight ${roasColor(r.total.roas)}`}>{r.total.roas.toFixed(2)}x</div>}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="sticky bottom-0 bg-white z-10">
            <tr className="border-t-2 border-gray-200">
              <td className="px-4 py-3 font-semibold text-gray-900 sticky left-0 bg-white z-20">TOPLAM</td>
              {periods.map(p => (
                <td key={p} className="px-4 py-3 text-right whitespace-nowrap">
                  <div className="font-semibold text-green-700">{money(footer.byPeriod[p].revenue)}</div>
                  {footer.byPeriod[p].cost > 0 && <div className="text-[10px] text-red-400 leading-tight">maliyet: {money(footer.byPeriod[p].cost)}</div>}
                </td>
              ))}
              <td className="px-4 py-3 text-right whitespace-nowrap">
                <div className="font-semibold text-green-700">{money(footer.revenue)}</div>
                {footer.cost > 0 && <div className="text-[10px] text-red-400 leading-tight">maliyet: {money(footer.cost)}</div>}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {filtered.length > visible.length && (
        <div className="p-4 border-t text-center">
          <button onClick={() => setShowAll(true)}
            className="text-sm font-medium text-blue-600 hover:text-blue-700">
            Tümünü göster ({fmtNum(filtered.length)} kampanya)
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Main Dashboard ── */
export default function BQAnalyticsDashboard() {
  const [tab, setTab] = useState('overview');
  const [preset, setPreset] = useState('last90');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const { startDate, endDate } = useMemo(() => {
    if (preset === 'custom' && customStart && customEnd)
      return { startDate: customStart, endDate: customEnd };
    return getDateRange(preset);
  }, [preset, customStart, customEnd]);

  const TabComponent = useMemo(() => {
    switch (tab) {
      case 'overview': return OverviewTab;
      case 'traffic': return TrafficTab;
      case 'devices': return DevicesGeoTab;
      case 'pages': return PagesTab;
      case 'events': return EventsTab;
      case 'ecommerce': return EcommerceTab;
      case 'banners': return BannersTab;
      default: return OverviewTab;
    }
  }, [tab]);

  return (
    <div className="p-6 space-y-6">
      {/* Header Card */}
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6">
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">BigQuery Analytics</h1>
              <p className="text-sm text-gray-500">{startDate} &rarr; {endDate}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Date Filter Buttons */}
            <div className="flex items-center gap-1 bg-gray-50 p-0.5 rounded-lg">
              {PRESETS.map(p => (
                <button key={p.key}
                  onClick={() => setPreset(p.key)}
                  className={`px-2.5 py-1 text-sm font-medium rounded-md transition-colors ${
                    preset === p.key
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-white hover:text-gray-800'
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>

            <button
              onClick={() => { /* Force re-render by toggling preset */ setPreset(p => p); }}
              className="p-2 text-gray-600 hover:text-blue-600 rounded-xl hover:bg-blue-50 transition-colors"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Custom Date Inputs */}
        {preset === 'custom' && (
          <div className="flex items-center gap-3 mt-2">
            <input type="date" value={customStart}
              onChange={e => setCustomStart(e.target.value)}
              className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            <span className="text-gray-400">-</span>
            <input type="date" value={customEnd}
              onChange={e => setCustomEnd(e.target.value)}
              className="bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
        )}
      </div>

      {/* Tab Selector */}
      <div className="w-full flex justify-center">
        <div className="inline-flex items-center gap-2 bg-white rounded-xl shadow-sm border border-gray-100 p-2">
          {TABS.map(t => {
            const TIcon = t.icon;
            return (
              <button key={t.key}
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  tab === t.key
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}>
                <TIcon className="w-4 h-4" />
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <TabComponent startDate={startDate} endDate={endDate} />

      {/* Daily Sales Table */}
      <DailySalesTable startDate={startDate} endDate={endDate} />

      {/* Weekly Sales & Revenue Table */}
      <WeeklySalesTable startDate={startDate} endDate={endDate} />

      {/* Monthly Sales & Revenue Table */}
      <MonthlySalesTable startDate={startDate} endDate={endDate} />

      {/* Month-over-Month Daily Line Chart (collapsible) */}
      <MonthOverMonthChart startDate={startDate} endDate={endDate} />

      {/* Daily Sales & Revenue Table (collapsible) */}
      <DailySalesRevenueTable startDate={startDate} endDate={endDate} />

      {/* Monthly Source Revenue Table */}
      <MonthlySourceTable startDate={startDate} endDate={endDate} />

      {/* Weekly Source Revenue Table — week-bucketed twin */}
      <WeeklySourceTable startDate={startDate} endDate={endDate} />

      {/* Monthly Source Revenue Table — Excluding Paid Channels */}
      <MonthlySourceTableNoCost startDate={startDate} endDate={endDate} />

      {/* Daily Channel Performance — With Cost (collapsible) */}
      <DailySourceTable startDate={startDate} endDate={endDate} />

      {/* Daily Channel Performance — Without Cost (collapsible) */}
      <DailySourceTableNoCost startDate={startDate} endDate={endDate} />

      {/* Week × Day metric matrix (day-of-week comparison across weeks) */}
      <WeekDayMatrixTable startDate={startDate} endDate={endDate} />

      {/* Campaign performance — BQ revenue next to platform cost */}
      <CampaignPerformanceTable startDate={startDate} endDate={endDate} />
    </div>
  );
}
